import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    CheckCircle, Search, Edit2, Trash2, Plus, ChevronDown,
    ChevronRight, X, ChevronUp, Calendar
} from 'lucide-react';
import InnerHeader from '../components/common/InnerHeader/InnerHeader';
import InnerFooter from '../components/common/InnerFooter/InnerFooter';
import AssetsSidebar from '../components/common/AssetsSidebar/AssetsSidebar';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal/ConfirmDeleteModal';
import './Dashboard.css';
import './AssetsList.css';
import './styles/pages-common.css';


const ROWS_PER_PAGE = 10;

const EMPTY_FORM = {
    category: '',      // numeric ID
    categoryName: '',  // display name, used for conditional UI logic
    associatedPoleId: '',
    // Core Details
    autoId: 'AP-SS0AQ-U-26-1N1-B001', id: '', description: '',
    purchaseDate: '', purchaseFrom: '', brand: '', model: '',
    //serialNo: '', department: '', assignedTo: '',
    vendorContact: '', vendorEmail: '', vendorPhone: '', invoiceNumber: '',
    poNumber: '', currency: '', cost: '',
    warrantyStart: '', warrantyEnd: '', warrantyTerms: '',
    commissionedDate: '', installationPartner: '',
    installedBy: '', installedOn: '', selectSite: '', installationLocation: '',
    latitude: '', longitude: '',
    fan_speed_rpm_cfm: '',

    // Technical Details (Pole)
    foundationType: '', poleHeight: '', poleMaterial: '', windRating: '',
    cameraEnabled: '', displayEnabled: '', solarEnabled: '', wifiEnabled: '', lightingEnabled: '',

    // Technical Details (Solar Light)
    autoDimming: '', batteryCapacity: '', beamAngleDegree: '', chargeControllerMode: '',
    colorTemperature: '', lightPosition: '', lumens: '', wattage: '',

    // Technical Details (Rain Hood)
    drainageDesign: '', hoodType: '', hoodMaterial: '',

    // Technical Details (LED Receiving Card)
    communicationType: '', maxResolutionSupported: '', refreshRateHz: '', supportedPixelCapacity: '',
    // Technical Details (LED SMPS)
    // expectedLifeHours (shared), position (shared), wifiEnabled (shared)

    // Technical Details (LED MS Cabinet)
    cabinetDepthMm: '', cabinetHeightMm: '', cabinetType: '', cabinetWidthMm: '', corrosionProtection: '',

    // Technical Details (Solar Battery)
    managementSystem: '', capacityAh: '', capacityKwh: '', expectedLifeCycles: '', expectedLifeYears: '',
    maxChargeVoltage: '', maxDischargeCurrent: '', nominalVoltage: '', recyclingPartner: '',
    // position (shared), type (shared), disposalMethod (shared), replacementThreshold (shared)

    // Technical Details (LED Controller)
    cloudConnected: '', communicationInterface: '', controlMode: '', controllerModel: '',
    inputInterfaces: '', maxPixelCapacity: '', outputPorts: '', supportedResolution: '',
    // position (shared)

    // Technical Details (Default/Other/Shared)
    position: '', type: '', disposalMethod: '', expectedLifeHours: '',
    mediaDescription: '', replacementThreshold: '',

    // Technical Details (Fan)
    airFlow: '', operatingTempRange: '', fanPower: '', fanRatedRpm: '',
    fanRole: '', speedControl: '',
    // wifiEnabled (shared), type (shared), position (shared)

    // Technical Details (Security Camera)
    cameraType: '', cameraConnectivity: '', frameRate: '', nightVision: '',
    cameraResolution: '', retentionDays: '',
    // wifiEnabled (shared)

    // Technical Details (Sensor)
    calibrationDate: '', commProtocol: '', firmwareVersion: '', metricsSupported: '',
    sensorOperatingRange: '', reportingFreqSeconds: '', sensorCategory: '',
    // wifiEnabled (shared), position (shared)

    // Technical Details (LED Screen)
    ledConnectivity: '', ledControllerModel: '', brightnessNits: '',
    ledRefreshRateHz: '', ledResolution: '', ledSize: '',
    // type (shared), position (shared)

    // Maintenance Details
    lastInspectionDate: '', lastServiceDate: '',
    nextInspectionDue: '', nextServiceDate: '',
    removedOn: '', replacementAssetId: '',
    notes: '',
};

// ─── Section toggle helper ────────────────────────────────────────────────────
const Section = ({ title, open, onToggle, children }) => (
    <div className="al-section">
        <div className="al-section-header" onClick={onToggle}>
            <span>{title}</span>
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
        {open && <div className="al-section-body">{children}</div>}
    </div>
);

// ─── Date Picker with Placeholder ────────────────────────────────────────────
const DatePicker = ({ name, placeholder, value, onChange }) => {
    const inputRef = useRef(null);
    const openPicker = () => {
        if (inputRef.current) {
            if (inputRef.current.showPicker) {
                inputRef.current.showPicker();
            } else {
                inputRef.current.click();
            }
        }
    };
    return (
        <div className="al-datepick-wrap" onClick={openPicker}>
            {/* Hidden native date input — provides the calendar popup */}
            <input
                ref={inputRef}
                className="al-datepick-hidden"
                type="date"
                name={name}
                value={value}
                onChange={onChange}
            />
            {/* Visible display row */}
            <div className="al-datepick-display">
                <span className={value ? 'al-datepick-value' : 'al-datepick-ph'}>
                    {value || placeholder}
                </span>
                <Calendar size={13} className="al-datepick-icon" />
            </div>
        </div>
    );
};

// ─── Detail Popup ─────────────────────────────────────────────────────────────
const getPopupRows = (asset) => {
    if (!asset) return [];
    // Detect type from prefixed keys present in the asset object
    if (asset.solar_light_id !== undefined) {
        return [
            { label: 'Solar Light ID', value: asset.solar_light_id },
            { label: 'Description', value: asset.solar_light_description },
            { label: 'Purchase Date', value: asset.solar_light_purchase_date },
            { label: 'Purchase From', value: asset.solar_light_purchased_from },
            { label: 'Brand', value: asset.solar_light_brand },
            { label: 'Model', value: asset.solar_light_model },
            { label: 'Wattage', value: asset.solar_light_wattage },
            { label: 'Lumens', value: asset.solar_light_lumens },
            { label: 'Battery Capacity', value: asset.solar_light_battery_capacity },
            { label: 'Light Position', value: asset.solar_light_light_position },
            { label: 'Color Temperature', value: asset.solar_light_color_temperature },
            { label: 'Beam Angle (deg)', value: asset.solar_light_beam_angle_degree },
            { label: 'Auto Dimming', value: asset.solar_light_auto_dimming ? 'Yes' : 'No' },
            { label: 'Charge Controller', value: asset.solar_light_charge_controller_mode ? 'Yes' : 'No' },
            { label: 'Warranty Start', value: asset.solar_light_warranty_start },
            { label: 'Warranty End', value: asset.solar_light_warranty_end },
            { label: 'Installed By', value: asset.solar_light_installed_by },
            { label: 'Installed On', value: asset.solar_light_installed_on },
            { label: 'Associated Pole', value: asset.solar_associated_pole },
        ];
    }
    if (asset.rain_hood_id !== undefined) {
        return [
            { label: 'Rain Hood ID', value: asset.rain_hood_id },
            { label: 'Description', value: asset.rain_hood_description },
            { label: 'Purchase Date', value: asset.rain_hood_purchase_date },
            { label: 'Purchase From', value: asset.rain_hood_purchased_from },
            { label: 'Brand', value: asset.rain_hood_brand },
            { label: 'Model', value: asset.rain_hood_model },
            { label: 'Material', value: asset.rain_hood_material },
            { label: 'Hood Type', value: asset.rain_hood_hood_type },
            { label: 'Drainage Design', value: asset.rain_hood_drainage_design },
            { label: 'Warranty Start', value: asset.rain_hood_warranty_start },
            { label: 'Warranty End', value: asset.rain_hood_warranty_end },
            { label: 'Installed By', value: asset.rain_hood_installed_by },
            { label: 'Installed On', value: asset.rain_hood_installed_on },
            { label: 'Associated Pole', value: asset.rain_hood_associated_pole },
        ];
    }
    if (asset.fan_id !== undefined) {
        return [
            { label: 'Fan ID', value: asset.fan_id },
            { label: 'Description', value: asset.fan_description },
            { label: 'Purchase Date', value: asset.fan_purchase_date },
            { label: 'Purchase From', value: asset.fan_purchased_from },
            { label: 'Brand', value: asset.fan_brand },
            { label: 'Model', value: asset.fan_model },
            { label: 'Air Flow', value: asset.fan_air_flow },
            { label: 'Power', value: asset.fan_power },
            { label: 'Rated RPM', value: asset.fan_rated_rpm },
            { label: 'Role', value: asset.fan_role },
            { label: 'Speed Control', value: asset.fan_speed_control },
            { label: 'Type', value: asset.fan_type },
            { label: 'Position', value: asset.fan_position },
            { label: 'Oper. Temp Range', value: asset.fan_operating_temperature_range },
            { label: 'WiFi Enabled', value: asset.fan_wifi_enabled ? 'Yes' : 'No' },
            { label: 'Warranty Start', value: asset.fan_warranty_start },
            { label: 'Warranty End', value: asset.fan_warranty_end },
            { label: 'Installed By', value: asset.fan_installed_by },
            { label: 'Installed On', value: asset.fan_installed_on },
            { label: 'Associated Pole', value: asset.fan_associated_pole },
        ];
    }
    if (asset.security_camera_id !== undefined) {
        return [
            { label: 'Camera ID', value: asset.security_camera_id },
            { label: 'Description', value: asset.security_camera_description },
            { label: 'Purchase Date', value: asset.security_camera_purchase_date },
            { label: 'Purchase From', value: asset.security_camera_purchased_from },
            { label: 'Brand', value: asset.security_camera_brand },
            { label: 'Model', value: asset.security_camera_model },
            { label: 'Camera Type', value: asset.security_camera_camera_type },
            { label: 'Resolution', value: asset.security_camera_resolution },
            { label: 'Frame Rate', value: asset.security_camera_frame_rate },
            { label: 'Connectivity', value: asset.security_camera_connectivity },
            { label: 'Retention Days', value: asset.security_camera_retention_days },
            { label: 'Night Vision', value: asset.security_camera_night_vision ? 'Yes' : 'No' },
            { label: 'WiFi Enabled', value: asset.security_wifi_enabled ? 'Yes' : 'No' },
            { label: 'Warranty Start', value: asset.security_camera_warranty_start },
            { label: 'Warranty End', value: asset.security_camera_warranty_end },
            { label: 'Installed By', value: asset.security_camera_installed_by },
            { label: 'Installed On', value: asset.security_camera_installed_on },
            { label: 'Associated Pole', value: asset.security_camera_associated_pole },
        ];
    }
    if (asset.sensor_id !== undefined) {
        return [
            { label: 'Sensor ID', value: asset.sensor_id },
            { label: 'Description', value: asset.sensor_description },
            { label: 'Purchase Date', value: asset.sensor_purchase_date },
            { label: 'Purchase From', value: asset.sensor_purchased_from },
            { label: 'Brand', value: asset.sensor_brand },
            { label: 'Model', value: asset.sensor_model },
            { label: 'Category', value: asset.sensor_category },
            { label: 'Communication Protocol', value: asset.sensor_communication_protocol },
            { label: 'Firmware Version', value: asset.sensor_firmware_version },
            { label: 'Metrics Supported', value: asset.sensor_metrics_supported },
            { label: 'Operating Range', value: asset.sensor_operating_range },
            { label: 'Reporting Freq (sec)', value: asset.sensor_reporting_frequency_seconds },
            { label: 'Position', value: asset.sensor_position },
            { label: 'WiFi Enabled', value: asset.sensor_wifi_enabled ? 'Yes' : 'No' },
            { label: 'Calibration Date', value: asset.sensor_calibration_date },
            { label: 'Warranty Start', value: asset.sensor_warranty_start },
            { label: 'Warranty End', value: asset.sensor_warranty_end },
            { label: 'Installed By', value: asset.sensor_installed_by },
            { label: 'Installed On', value: asset.sensor_installed_on },
            { label: 'Associated Pole', value: asset.sensor_associated_pole },
        ];
    }
    if (asset.cartridge_id !== undefined) {
        return [
            { label: 'Cartridge ID', value: asset.cartridge_id },
            { label: 'Description', value: asset.cartridge_description },
            { label: 'Purchase Date', value: asset.cartridge_purchase_date },
            { label: 'Purchase From', value: asset.cartridge_purchased_from },
            { label: 'Brand', value: asset.cartridge_brand },
            { label: 'Model', value: asset.cartridge_model },
            { label: 'Type', value: asset.cartridge_type },
            { label: 'Position', value: asset.cartridge_position },
            { label: 'Disposal Method', value: asset.cartridge_disposal_method },
            { label: 'Expected Life (hrs)', value: asset.cartridge_expected_life_hours },
            { label: 'Media Description', value: asset.cartridge_media_description },
            { label: 'Replacement Threshold', value: asset.cartridge_replacement_threshold },
            { label: 'Warranty Start', value: asset.cartridge_warranty_start },
            { label: 'Warranty End', value: asset.cartridge_warranty_end },
            { label: 'Installed By', value: asset.cartridge_installed_by },
            { label: 'Installed On', value: asset.cartridge_installed_on },
            { label: 'Associated Pole', value: asset.cartridge_associated_pole },
        ];
    }
    if (asset.led_screen_id !== undefined) {
        return [
            { label: 'LED Screen ID', value: asset.led_screen_id },
            { label: 'Description', value: asset.led_screen_description },
            { label: 'Purchase Date', value: asset.led_screen_purchase_date },
            { label: 'Purchase From', value: asset.led_screen_purchased_from },
            { label: 'Brand', value: asset.led_screen_brand },
            { label: 'Model', value: asset.led_screen_model },
            { label: 'Resolution', value: asset.led_screen_resolution },
            { label: 'Size', value: asset.led_screen_size },
            { label: 'Brightness (nits)', value: asset.led_screen_brightness_nits },
            { label: 'Refresh Rate (Hz)', value: asset.led_screen_refresh_rate_hz },
            { label: 'Connectivity', value: asset.led_screen_connectivity },
            { label: 'Controller Model', value: asset.led_screen_controller_model },
            { label: 'Type', value: asset.led_screen_type },
            { label: 'Position', value: asset.led_screen_position },
            { label: 'Warranty Start', value: asset.led_screen_warranty_start },
            { label: 'Warranty End', value: asset.led_screen_warranty_end },
            { label: 'Installed By', value: asset.led_screen_installed_by },
            { label: 'Installed On', value: asset.led_screen_installed_on },
            { label: 'Associated Pole', value: asset.led_screen_associated_pole },
        ];
    }
    if (asset.led_controller_id !== undefined) {
        return [
            { label: 'Controller ID', value: asset.led_controller_id },
            { label: 'Description', value: asset.led_controller_description },
            { label: 'Purchase Date', value: asset.led_controller_purchase_date },
            { label: 'Purchase From', value: asset.led_controller_purchased_from },
            { label: 'Brand', value: asset.led_controller_brand },
            { label: 'Model', value: asset.led_controller_model },
            { label: 'Cloud Connected', value: asset.led_controller_cloud_connected ? 'Yes' : 'No' },
            { label: 'Comm. Interface', value: asset.led_controller_communication_interface },
            { label: 'Control Mode', value: asset.led_controller_control_mode },
            { label: 'Position', value: asset.led_controller_position },
            { label: 'Input Interfaces', value: asset.led_controller_input_interfaces },
            { label: 'Max Pixel Capacity', value: asset.led_controller_max_pixel_capacity },
            { label: 'Output Ports', value: asset.led_controller_output_ports },
            { label: 'Supported Resolution', value: asset.led_controller_supported_resolution },
            { label: 'Warranty Start', value: asset.led_controller_warranty_start },
            { label: 'Warranty End', value: asset.led_controller_warranty_end },
            { label: 'Installed By', value: asset.led_controller_installed_by },
            { label: 'Installed On', value: asset.led_controller_installed_on },
            { label: 'Associated Pole', value: asset.led_controller_associated_pole },
        ];
    }
    if (asset.led_ms_cabinet_id !== undefined) {
        return [
            { label: 'Cabinet ID', value: asset.led_ms_cabinet_id },
            { label: 'Description', value: asset.led_ms_cabinet_description },
            { label: 'Purchase Date', value: asset.led_ms_cabinet_purchase_date },
            { label: 'Purchase From', value: asset.led_ms_cabinet_purchased_from },
            { label: 'Brand', value: asset.led_ms_cabinet_brand },
            { label: 'Model', value: asset.led_ms_cabinet_model },
            { label: 'Type', value: asset.led_ms_cabinet_type },
            { label: 'Protection', value: asset.led_ms_cabinet_protection },
            { label: 'Depth', value: asset.led_ms_cabinet_depth },
            { label: 'Height', value: asset.led_ms_cabinet_height },
            { label: 'Width', value: asset.led_ms_cabinet_width },
            { label: 'Warranty Start', value: asset.led_ms_cabinet_warranty_start },
            { label: 'Warranty End', value: asset.led_ms_cabinet_warranty_end },
            { label: 'Installed By', value: asset.led_ms_cabinet_installed_by },
            { label: 'Installed On', value: asset.led_ms_cabinet_installed_on },
            { label: 'Associated Pole', value: asset.led_ms_cabinet_associated_pole },
        ];
    }
    if (asset.led_receiving_card_id !== undefined) {
        return [
            { label: 'Receiving Card ID', value: asset.led_receiving_card_id },
            { label: 'Description', value: asset.led_receiving_card_description },
            { label: 'Purchase Date', value: asset.led_receiving_card_purchase_date },
            { label: 'Purchase From', value: asset.led_receiving_card_purchased_from },
            { label: 'Brand', value: asset.led_receiving_card_brand },
            { label: 'Model', value: asset.led_receiving_card_model },
            { label: 'Warranty Start', value: asset.led_receiving_card_warranty_start },
            { label: 'Warranty End', value: asset.led_receiving_card_warranty_end },
            { label: 'Installed By', value: asset.led_receiving_card_installed_by },
            { label: 'Installed On', value: asset.led_receiving_card_installed_on },
            { label: 'Associated Pole', value: asset.led_receiving_card_associated_pole },
        ];
    }
    if (asset.led_smps_id !== undefined) {
        return [
            { label: 'SMPS ID', value: asset.led_smps_id },
            { label: 'Description', value: asset.led_smps_description },
            { label: 'Purchase Date', value: asset.led_smps_purchase_date },
            { label: 'Purchase From', value: asset.led_smps_purchased_from },
            { label: 'Brand', value: asset.led_smps_brand },
            { label: 'Model', value: asset.led_smps_model },
            { label: 'Warranty Start', value: asset.led_smps_warranty_start },
            { label: 'Warranty End', value: asset.led_smps_warranty_end },
            { label: 'Installed By', value: asset.led_smps_installed_by },
            { label: 'Installed On', value: asset.led_smps_installed_on },
            { label: 'Associated Pole', value: asset.led_smps_associated_pole },
        ];
    }
    if (asset.solar_battery_id !== undefined) {
        return [
            { label: 'Battery ID', value: asset.solar_battery_id },
            { label: 'Description', value: asset.solar_battery_description },
            { label: 'Purchase Date', value: asset.solar_battery_purchase_date },
            { label: 'Purchase From', value: asset.solar_battery_purchased_from },
            { label: 'Brand', value: asset.solar_battery_brand },
            { label: 'Model', value: asset.solar_battery_model },
            { label: 'Warranty Start', value: asset.solar_battery_warranty_start },
            { label: 'Warranty End', value: asset.solar_battery_warranty_end },
            { label: 'Installed By', value: asset.solar_battery_installed_by },
            { label: 'Installed On', value: asset.solar_battery_installed_on },
            { label: 'Associated Pole', value: asset.solar_battery_associated_pole },
        ];
    }
    // Default: Pole fields
    return [
        { label: 'Pole ID', value: asset.pole_id },
        { label: 'Description', value: asset.pole_description },
        { label: 'Purchase Date', value: asset.pole_purchase_date },
        { label: 'Purchase From', value: asset.pole_purchased_from },
        { label: 'Brand', value: asset.pole_brand },
        { label: 'Model', value: asset.pole_model },
        { label: 'Height', value: asset.pole_height },
        { label: 'Material', value: asset.pole_material },
        { label: 'Foundation', value: asset.pole_foundation_type },
        { label: 'Warranty Start', value: asset.pole_warranty_start },
        { label: 'Warranty End', value: asset.pole_warranty_end },
        { label: 'Installed By', value: asset.pole_installed_by },
        { label: 'Installed On', value: asset.pole_installed_on },
        { label: 'Location', value: String(asset.pole_installation_location || '') },
        { label: 'Category', value: String(asset.category || '') },
        { label: 'Fan Speed (RPM/CFM)', value: asset.pole_fan_speed_rpm_cfm ?? asset.fan_speed_rpm_cfm },
    ];
};

const DetailPopup = ({ asset, isLoading, onClose }) => {
    const rows = getPopupRows(asset);
    const popupTitle =
        asset?.pole_id ||
        asset?.solar_light_id ||
        asset?.rain_hood_id ||
        asset?.fan_id ||
        asset?.security_camera_id ||
        asset?.sensor_id ||
        asset?.cartridge_id ||
        asset?.led_screen_id ||
        asset?.led_controller_id ||
        asset?.led_ms_cabinet_id ||
        asset?.led_receiving_card_id ||
        asset?.led_smps_id ||
        asset?.solar_battery_id ||
        asset?.id ||
        'Loading...';


    return (
        <div className="al-overlay" onClick={onClose}>
            <motion.div
                className="al-popup"
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={e => e.stopPropagation()}
            >
                <div className="al-popup-head">
                    <span className="al-popup-title">{popupTitle}</span>
                    <button className="al-popup-close" onClick={onClose}><X size={14} /></button>
                </div>
                <div className="al-popup-body">
                    {isLoading ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>Loading...</div>
                    ) : (
                        rows.map(r => (
                            <div key={r.label} className="al-popup-row">
                                <span className="al-popup-label">{r.label}</span>
                                <span className="al-popup-value">{r.value ?? '—'}</span>
                            </div>
                        ))
                    )}
                </div>
            </motion.div>
        </div>
    );
};

// ─── Component ────────────────────────────────────────────────────────────────
export const AssetsList = ({ setView }) => {
    const [user, setUser] = useState(() => {
        try {
            const stored = localStorage.getItem('user');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    });
    const [alert, setAlert] = useState(null);
    const [assets, setAssets] = useState(null);
    const [categories, setCategories] = useState([]);
    const [sitesList, setSitesList] = useState([]);
    const [locationsList, setLocationsList] = useState([]);
    const [polesList, setPolesList] = useState([]);
    const [suppliersList, setSuppliersList] = useState([]);
    const [filterCategory, setFilterCategory] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTopNav, setActiveTopNav] = useState('Asset');

    // View state: 'list' | 'form'
    const [viewMode, setViewMode] = useState('list');
    const [editingAsset, setEditingAsset] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState({});

    // Collapsible form sections
    const [catOpen, setCatOpen] = useState(true);
    const [coreOpen, setCoreOpen] = useState(true);
    const [techOpen, setTechOpen] = useState(true);
    const [maintOpen, setMaintOpen] = useState(true);

    // Detail popup
    const [popupAsset, setPopupAsset] = useState(null);
    const [popupLoading, setPopupLoading] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null); // { parentIdx, childId }

    // ── Fetch user ──────────────────────────────────────────────────────────────
    const fetchUserDetails = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return null;
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/AlluserDetails/`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            });
            if (!res.ok) throw new Error('Failed');
            const data = await res.json();
            if (data.status && data.data?.user) {
                const u = data.data.user;
                if (u.profile_picture && !u.picture) u.picture = u.profile_picture;
                setUser(u);
                localStorage.setItem('user', JSON.stringify(u));
                return u;
            }
            throw new Error('Invalid');
        } catch {
            const stored = localStorage.getItem('user');
            if (stored) { const u = JSON.parse(stored); setUser(u); return u; }
            return null;
        }
    };

    const fetchCategories = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return;
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/category/`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            });
            if (!res.ok) {
                console.error('[Categories] API error:', res.status);
                return;
            }
            const data = await res.json();
            let list = [];
            if (data && data.status && Array.isArray(data.data)) list = data.data;
            else if (Array.isArray(data)) list = data;
            else if (data && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.results)) list = data.results;
            const mapped = list.map(c => ({
                id: String(c.id ?? c.category_id ?? c.pk ?? ''),
                name: c.category_name || c.name || '',
            }));
            setCategories(mapped);
        } catch (err) {
            console.error('Failed to fetch categories', err);
        }
    };

    const fetchSites = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return;
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sites/`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            });
            if (!res.ok) return;
            const data = await res.json();
            if (data && data.status && Array.isArray(data.data)) {
                setSitesList(data.data.map(s => ({ id: s.id || s.site_id, name: s.site_name || '' })));
            }
        } catch (err) {
            console.error('Failed to fetch sites', err);
        }
    };

    const fetchLocations = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return;
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/locations/`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            });
            if (!res.ok) return;
            const data = await res.json();
            if (data && data.status && Array.isArray(data.data)) {
                setLocationsList(data.data.map(l => ({
                    id: l.id || l.location_id,
                    siteId: l.site || l.site_id,
                    name: l.location_name || '',
                })));
            }
        } catch (err) {
            console.error('Failed to fetch locations', err);
        }
    };

    const fetchSuppliers = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return;
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/suppliers/`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            });
            if (!res.ok) return;
            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) list = data;
            else if (data && data.status && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.results)) list = data.results;
            const mapped = list.map(item => ({
                id: item.supplier_id ?? item.id,
                code: item.supplier_code ?? item.code ?? '',
                name: item.supplier_name ?? item.name ?? '',
                email: item.email ?? '',
                phone: item.telephone ?? item.phone ?? '',
                telephone: item.telephone ?? item.phone ?? '',
            })).filter(s => s.name);
            setSuppliersList(mapped);
        } catch (err) {
            console.error('Failed to fetch suppliers', err);
        }
    };

    // ── Open Asset Detail Popup (routes to type-specific single-record endpoint) ──
    const ASSET_ENDPOINTS = {
        'Pole': (id) => `${process.env.REACT_APP_API_BASE_URL}/poles/single-record/${id}/`,
        'Solar Light': (id) => `${process.env.REACT_APP_API_BASE_URL}/solar-lights/single-record/${id}/`,
        'Rain Hood': (id) => `${process.env.REACT_APP_API_BASE_URL}/rainhood/single-record/${id}/`,
        'Fan': (id) => `${process.env.REACT_APP_API_BASE_URL}/fan/single-record/${id}/`,
        'Security Camera': (id) => `${process.env.REACT_APP_API_BASE_URL}/security-cameras/single-record/${id}/`,
        'Sensor': (id) => `${process.env.REACT_APP_API_BASE_URL}/sensor/single-record/${id}/`,
        'Cartridge': (id) => `${process.env.REACT_APP_API_BASE_URL}/cartridge/single-record/${id}/`,
        'LED Screen': (id) => `${process.env.REACT_APP_API_BASE_URL}/led-screen/single-record/${id}/`,
        'LED Controller': (id) => `${process.env.REACT_APP_API_BASE_URL}/led-controller/single-record/${id}/`,
        'LED MS Cabinet': (id) => `${process.env.REACT_APP_API_BASE_URL}/led-ms-cabinet/single-record/${id}/`,
        'LED Receiving Card': (id) => `${process.env.REACT_APP_API_BASE_URL}/led-receiving-card/single-record/${id}/`,
        'LED SMPS': (id) => `${process.env.REACT_APP_API_BASE_URL}/led-smps/single-record/${id}/`,
        'Solar Battery': (id) => `${process.env.REACT_APP_API_BASE_URL}/solar-battery/single-record/${id}/`,
    };

    const openAssetPopup = async (assetType, assetId) => {
        setPopupLoading(true);
        setPopupAsset({}); // open popup immediately to show loading
        try {
            const accessToken = localStorage.getItem('accessToken');
            const getUrl = ASSET_ENDPOINTS[assetType];
            if (!getUrl) {
                console.warn('[Popup] No endpoint for type:', assetType);
                setPopupAsset(null);
                return;
            }
            const url = `${getUrl(assetId)}?t=${Date.now()}`;
            const res = await fetch(url, {
                method: 'GET',
                cache: 'no-store',
                headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            });

            if (!res.ok) throw new Error(`Failed to fetch ${assetType} detail`);
            const json = await res.json();
            // All single-record APIs return { status: true, data: { ... } }
            const data = json.data || json;
            setPopupAsset(data);
        } catch (err) {
            console.error('[Popup] Failed to fetch asset detail:', err);
            setPopupAsset(null);
        } finally {
            setPopupLoading(false);
        }
    };

    const fetchAssets = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return;
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/poles-assets/`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            });
            if (!res.ok) { console.error('[Assets] API error:', res.status); return; }

            const json = await res.json();

            // API returns: { status: true, data: [{ pole_id, solar_light_ids, rain_hood_ids, ... }] }
            let list = [];
            if (json && Array.isArray(json.data)) list = json.data;
            else if (Array.isArray(json)) list = json;
            else if (json && Array.isArray(json.results)) list = json.results;

            // Populate polesList for the Associated Pole ID dropdown
            const ids = list.map(item => item.pole_id || '').filter(Boolean);
            setPolesList(ids);

            // Map each pole → parent row; build children from the ID arrays
            const ASSET_TYPE_MAP = [
                { key: 'solar_light_ids', label: 'Solar Light' },
                { key: 'rain_hood_ids', label: 'Rain Hood' },
                { key: 'security_camera_ids', label: 'Security Camera' },
                { key: 'fan_ids', label: 'Fan' },
                { key: 'sensor_ids', label: 'Sensor' },
                { key: 'cartridge_ids', label: 'Cartridge' },
                { key: 'led_screen_ids', label: 'LED Screen' },
                { key: 'led_controller_ids', label: 'LED Controller' },
                { key: 'led_ms_cabinet_ids', label: 'LED MS Cabinet' },
                { key: 'led_receiving_card_ids', label: 'LED Receiving Card' },
                { key: 'led_smps_ids', label: 'LED SMPS' },
                { key: 'solar_battery_ids', label: 'Solar Battery' },
            ];

            const mapped = list.map(item => {
                const poleId = item.pole_id || '';

                // First child = the pole itself
                const children = [{
                    id: poleId,
                    childType: 'Pole',
                    category: 'Pole',
                    status: item.status || '',
                }];

                // Add child rows for each associated asset type
                ASSET_TYPE_MAP.forEach(({ key, label }) => {
                    const assetIds = item[key];
                    if (Array.isArray(assetIds)) {
                        assetIds.forEach(assetId => {
                            children.push({
                                id: assetId,
                                childType: label,
                                category: label,
                                status: '',
                            });
                        });
                    }
                });

                return {
                    id: poleId,
                    expanded: false,
                    status: item.status ?? true,
                    rawData: item,
                    children,
                };
            });

            setAssets(mapped);
        } catch (err) {
            console.error('[Assets] Failed to fetch:', err);
        }
    };

    useEffect(() => {
        let isMounted = true;

        fetchUserDetails().then(userData => {
            if (!userData && isMounted) {
                setAlert({ type: 'error', message: 'Error loading user information' });
                setTimeout(() => setView && setView('signin'), 500);
            }
        });

        // Run metadata and data queries concurrently in the background
        fetchCategories();
        fetchSites();
        fetchLocations();
        fetchSuppliers();
        fetchAssets();

        return () => {
            isMounted = false;
        };
    }, [setView]);

    // ── Toggle parent status ────────────────────────────────────────────────
    const toggleStatus = (idx, e) => {
        e.stopPropagation();
        setAssets(prev => prev ? prev.map((a, i) => i === idx ? { ...a, status: !a.status } : a) : prev);
    };

    // ── Toggle parent row expand ─────────────────────────────────────────────
    const toggleExpand = (idx) => {
        setAssets(prev => prev ? prev.map((a, i) => i === idx ? { ...a, expanded: !a.expanded } : a) : prev);
    };

    // ── Filtered assets ──────────────────────────────────────────────────────
    const filtered = (assets || []).filter(a =>
        (!searchQuery || a.id.toLowerCase().includes(searchQuery.toLowerCase())) &&
        (!filterCategory || a.children.some(c => c.category === filterCategory))
    );
    const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
    const paginated = filtered.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);

    // ── Open Add form ────────────────────────────────────────────────────────
    const openAddForm = () => {
        setEditingAsset(null);
        setFormData(EMPTY_FORM);
        setFormErrors({});
        setViewMode('form');
        fetchSuppliers();
    };

    // ── Open Edit form ───────────────────────────────────────────────────────
    const openEditForm = (child, rawData) => {
        fetchSuppliers();
        setEditingAsset(child);
        const cat = child.category || '';
        const matchedCat = categories.find(c => c.name === cat);
        const r = rawData || {};

        // Helper: pick value by category-specific prefix first, fallback to child prop
        const fromR = (prefixedKey, fallback = '') => r[prefixedKey] != null ? r[prefixedKey] : fallback;
        const bool2radio = v => v === true ? 'Yes' : v === false ? 'No' : '';

        let common = {};
        if (cat === 'Sensor') {
            common = {
                id: fromR('sensor_id', child.id),
                description: fromR('sensor_description'),
                purchaseDate: fromR('sensor_purchase_date'),
                purchaseFrom: fromR('sensor_purchased_from'),
                brand: fromR('sensor_brand'),
                model: fromR('sensor_model'),
                vendorContact: fromR('sensor_vendor_contact_name'),
                vendorEmail: fromR('sensor_vendor_email'),
                vendorPhone: fromR('sensor_vendor_phone_number'),
                invoiceNumber: fromR('sensor_invoice_number'),
                poNumber: fromR('sensor_po_number'),
                currency: fromR('sensor_currency'),
                cost: fromR('sensor_cost'),
                warrantyStart: fromR('sensor_warranty_start'),
                warrantyEnd: fromR('sensor_warranty_end'),
                warrantyTerms: fromR('sensor_warranty_terms'),
                commissionedDate: fromR('sensor_commissioned_date'),
                installationPartner: fromR('sensor_installation_partner'),
                installedBy: fromR('sensor_installed_by'),
                installedOn: fromR('sensor_installed_on'),
                associatedPoleId: fromR('sensor_associated_pole'),
                // Sensor-specific
                calibrationDate: fromR('sensor_calibration_date'),
                commProtocol: fromR('sensor_communication_protocol'),
                firmwareVersion: fromR('sensor_firmware_version'),
                metricsSupported: fromR('sensor_metrics_supported'),
                sensorOperatingRange: fromR('sensor_operating_range'),
                reportingFreqSeconds: fromR('sensor_reporting_frequency_seconds'),
                sensorCategory: fromR('sensor_category'),
                position: fromR('sensor_position'),
                wifiEnabled: bool2radio(r.sensor_wifi_enabled),
            };
        } else if (cat === 'Solar Light') {
            common = {
                id: fromR('solar_light_id', child.id),
                description: fromR('solar_light_description'),
                purchaseDate: fromR('solar_light_purchase_date'),
                purchaseFrom: fromR('solar_light_purchased_from'),
                brand: fromR('solar_light_brand'),
                model: fromR('solar_light_model'),
                vendorContact: fromR('solar_light_vendor_contact_name'),
                vendorEmail: fromR('solar_light_vendor_email'),
                vendorPhone: fromR('solar_light_vendor_phone_number'),
                invoiceNumber: fromR('solar_light_invoice_number'),
                poNumber: fromR('solar_light_po_number'),
                currency: fromR('solar_light_currency'),
                cost: fromR('solar_light_cost'),
                warrantyStart: fromR('solar_light_warranty_start'),
                warrantyEnd: fromR('solar_light_warranty_end'),
                warrantyTerms: fromR('solar_light_warranty_terms'),
                commissionedDate: fromR('solar_light_commissioned_date'),
                installationPartner: fromR('solar_light_installation_partner'),
                installedBy: fromR('solar_light_installed_by'),
                installedOn: fromR('solar_light_installed_on'),
                associatedPoleId: fromR('solar_associated_pole'),
                autoDimming: bool2radio(r.solar_light_auto_dimming),
                batteryCapacity: fromR('solar_light_battery_capacity'),
                beamAngleDegree: fromR('solar_light_beam_angle_degree'),
                chargeControllerMode: bool2radio(r.solar_light_charge_controller_mode),
                colorTemperature: fromR('solar_light_color_temperature'),
                lightPosition: fromR('solar_light_light_position'),
                lumens: fromR('solar_light_lumens'),
                wattage: fromR('solar_light_wattage'),
            };
        } else if (cat === 'Security Camera') {
            common = {
                id: fromR('security_camera_id', child.id),
                description: fromR('security_camera_description'),
                purchaseDate: fromR('security_camera_purchase_date'),
                purchaseFrom: fromR('security_camera_purchased_from'),
                brand: fromR('security_camera_brand'),
                model: fromR('security_camera_model'),
                vendorContact: fromR('security_camera_vendor_contact_name'),
                vendorEmail: fromR('security_camera_vendor_email'),
                vendorPhone: fromR('security_camera_vendor_phone_number'),
                invoiceNumber: fromR('security_camera_invoice_number'),
                poNumber: fromR('security_camera_po_number'),
                currency: fromR('security_camera_currency'),
                cost: fromR('security_camera_cost'),
                warrantyStart: fromR('security_camera_warranty_start'),
                warrantyEnd: fromR('security_camera_warranty_end'),
                warrantyTerms: fromR('security_camera_warranty_terms'),
                commissionedDate: fromR('security_camera_commissioned_date'),
                installationPartner: fromR('security_camera_installation_partner'),
                installedBy: fromR('security_camera_installed_by'),
                installedOn: fromR('security_camera_installed_on'),
                associatedPoleId: fromR('security_camera_associated_pole'),
                cameraType: fromR('security_camera_camera_type'),
                cameraConnectivity: fromR('security_camera_connectivity'),
                frameRate: fromR('security_camera_frame_rate'),
                nightVision: bool2radio(r.security_camera_night_vision),
                cameraResolution: fromR('security_camera_resolution'),
                retentionDays: fromR('security_camera_retention_days'),
                wifiEnabled: bool2radio(r.security_wifi_enabled),
            };
        } else if (cat === 'Rain Hood') {
            common = {
                id: fromR('rain_hood_id', child.id),
                description: fromR('rain_hood_description'),
                purchaseDate: fromR('rain_hood_purchase_date'),
                purchaseFrom: fromR('rain_hood_purchased_from'),
                brand: fromR('rain_hood_brand'),
                model: fromR('rain_hood_model'),
                vendorContact: fromR('rain_hood_vendor_contact_name'),
                vendorEmail: fromR('rain_hood_vendor_email'),
                vendorPhone: fromR('rain_hood_vendor_phone_number'),
                invoiceNumber: fromR('rain_hood_invoice_number'),
                poNumber: fromR('rain_hood_po_number'),
                currency: fromR('rain_hood_currency'),
                cost: fromR('rain_hood_cost'),
                warrantyStart: fromR('rain_hood_warranty_start'),
                warrantyEnd: fromR('rain_hood_warranty_end'),
                warrantyTerms: fromR('rain_hood_warranty_terms'),
                commissionedDate: fromR('rain_hood_commissioned_date'),
                installationPartner: fromR('rain_hood_installation_partner'),
                installedBy: fromR('rain_hood_installed_by'),
                installedOn: fromR('rain_hood_installed_on'),
                associatedPoleId: fromR('rain_hood_associated_pole'),
                drainageDesign: fromR('rain_hood_drainage_design'),
                hoodType: fromR('rain_hood_hood_type'),
                hoodMaterial: fromR('rain_hood_material'),
            };
        } else if (cat === 'Fan') {
            common = {
                id: fromR('fan_id', child.id),
                description: fromR('fan_description'),
                purchaseDate: fromR('fan_purchase_date'),
                purchaseFrom: fromR('fan_purchased_from'),
                brand: fromR('fan_brand'),
                model: fromR('fan_model'),
                vendorContact: fromR('fan_vendor_contact_name'),
                vendorEmail: fromR('fan_vendor_email'),
                vendorPhone: fromR('fan_vendor_phone_number'),
                invoiceNumber: fromR('fan_invoice_number'),
                poNumber: fromR('fan_po_number'),
                currency: fromR('fan_currency'),
                cost: fromR('fan_cost'),
                warrantyStart: fromR('fan_warranty_start'),
                warrantyEnd: fromR('fan_warranty_end'),
                warrantyTerms: fromR('fan_warranty_terms'),
                commissionedDate: fromR('fan_commissioned_date'),
                installationPartner: fromR('fan_installation_partner'),
                installedBy: fromR('fan_installed_by'),
                installedOn: fromR('fan_installed_on'),
                associatedPoleId: fromR('fan_associated_pole'),
                airFlow: fromR('fan_air_flow'),
                operatingTempRange: fromR('fan_operating_temperature_range'),
                position: fromR('fan_position'),
                fanPower: fromR('fan_power'),
                fanRatedRpm: fromR('fan_rated_rpm'),
                fanRole: fromR('fan_role'),
                speedControl: fromR('fan_speed_control'),
                type: fromR('fan_type'),
                wifiEnabled: bool2radio(r.fan_wifi_enabled),
            };
        } else if (cat === 'Cartridge') {
            common = {
                id: fromR('cartridge_id', child.id),
                description: fromR('cartridge_description'),
                purchaseDate: fromR('cartridge_purchase_date'),
                purchaseFrom: fromR('cartridge_purchased_from'),
                brand: fromR('cartridge_brand'),
                model: fromR('cartridge_model'),
                vendorContact: fromR('cartridge_vendor_contact_name'),
                vendorEmail: fromR('cartridge_vendor_email'),
                vendorPhone: fromR('cartridge_vendor_phone_number'),
                invoiceNumber: fromR('cartridge_invoice_number'),
                poNumber: fromR('cartridge_po_number'),
                currency: fromR('cartridge_currency'),
                cost: fromR('cartridge_cost'),
                warrantyStart: fromR('cartridge_warranty_start'),
                warrantyEnd: fromR('cartridge_warranty_end'),
                warrantyTerms: fromR('cartridge_warranty_terms'),
                commissionedDate: fromR('cartridge_commissioned_date'),
                installationPartner: fromR('cartridge_installation_partner'),
                installedBy: fromR('cartridge_installed_by'),
                installedOn: fromR('cartridge_installed_on'),
                associatedPoleId: fromR('cartridge_associated_pole'),
                position: fromR('cartridge_position'),
                type: fromR('cartridge_type'),
                disposalMethod: fromR('cartridge_disposal_method'),
                expectedLifeHours: fromR('cartridge_expected_life_hours'),
                mediaDescription: fromR('cartridge_media_description'),
                replacementThreshold: fromR('cartridge_replacement_threshold'),
            };
        } else if (cat === 'LED Screen') {
            common = {
                id: fromR('led_screen_id', child.id),
                description: fromR('led_screen_description'),
                purchaseDate: fromR('led_screen_purchase_date'),
                purchaseFrom: fromR('led_screen_purchased_from'),
                brand: fromR('led_screen_brand'),
                model: fromR('led_screen_model'),
                vendorContact: fromR('led_screen_vendor_contact_name'),
                vendorEmail: fromR('led_screen_vendor_email'),
                vendorPhone: fromR('led_screen_vendor_phone_number'),
                invoiceNumber: fromR('led_screen_invoice_number'),
                poNumber: fromR('led_screen_po_number'),
                currency: fromR('led_screen_currency'),
                cost: fromR('led_screen_cost'),
                warrantyStart: fromR('led_screen_warranty_start'),
                warrantyEnd: fromR('led_screen_warranty_end'),
                warrantyTerms: fromR('led_screen_warranty_terms'),
                commissionedDate: fromR('led_screen_commissioned_date'),
                installationPartner: fromR('led_screen_installation_partner'),
                installedBy: fromR('led_screen_installed_by'),
                installedOn: fromR('led_screen_installed_on'),
                associatedPoleId: fromR('led_screen_associated_pole'),
                ledConnectivity: fromR('led_screen_connectivity'),
                ledControllerModel: fromR('led_screen_controller_model'),
                brightnessNits: fromR('led_screen_brightness_nits'),
                position: fromR('led_screen_position'),
                ledRefreshRateHz: fromR('led_screen_refresh_rate_hz'),
                ledResolution: fromR('led_screen_resolution'),
                ledSize: fromR('led_screen_size'),
                type: fromR('led_screen_type'),
            };
        } else if (cat === 'LED Controller') {
            common = {
                id: fromR('led_controller_id', child.id),
                description: fromR('led_controller_description'),
                purchaseDate: fromR('led_controller_purchase_date'),
                purchaseFrom: fromR('led_controller_purchased_from'),
                brand: fromR('led_controller_brand'),
                model: fromR('led_controller_model'),
                vendorContact: fromR('led_controller_vendor_contact_name'),
                vendorEmail: fromR('led_controller_vendor_email'),
                vendorPhone: fromR('led_controller_vendor_phone_number'),
                invoiceNumber: fromR('led_controller_invoice_number'),
                poNumber: fromR('led_controller_po_number'),
                currency: fromR('led_controller_currency'),
                cost: fromR('led_controller_cost'),
                warrantyStart: fromR('led_controller_warranty_start'),
                warrantyEnd: fromR('led_controller_warranty_end'),
                warrantyTerms: fromR('led_controller_warranty_terms'),
                commissionedDate: fromR('led_controller_commissioned_date'),
                installationPartner: fromR('led_controller_installation_partner'),
                installedBy: fromR('led_controller_installed_by'),
                installedOn: fromR('led_controller_installed_on'),
                associatedPoleId: fromR('led_controller_associated_pole'),
                cloudConnected: bool2radio(r.led_controller_cloud_connected),
                communicationInterface: fromR('led_controller_communication_interface'),
                controlMode: fromR('led_controller_control_mode'),
                position: fromR('led_controller_position'),
                inputInterfaces: fromR('led_controller_input_interfaces'),
                maxPixelCapacity: fromR('led_controller_max_pixel_capacity'),
                outputPorts: fromR('led_controller_output_ports'),
                supportedResolution: fromR('led_controller_supported_resolution'),
            };
        } else if (cat === 'LED MS Cabinet') {
            common = {
                id: fromR('led_ms_cabinet_id', child.id),
                description: fromR('led_ms_cabinet_description'),
                purchaseDate: fromR('led_ms_cabinet_purchase_date'),
                purchaseFrom: fromR('led_ms_cabinet_purchased_from'),
                brand: fromR('led_ms_cabinet_brand'),
                model: fromR('led_ms_cabinet_model'),
                vendorContact: fromR('led_ms_cabinet_vendor_contact_name'),
                vendorEmail: fromR('led_ms_cabinet_vendor_email'),
                vendorPhone: fromR('led_ms_cabinet_vendor_phone_number'),
                invoiceNumber: fromR('led_ms_cabinet_invoice_number'),
                poNumber: fromR('led_ms_cabinet_po_number'),
                currency: fromR('led_ms_cabinet_currency'),
                cost: fromR('led_ms_cabinet_cost'),
                warrantyStart: fromR('led_ms_cabinet_warranty_start'),
                warrantyEnd: fromR('led_ms_cabinet_warranty_end'),
                warrantyTerms: fromR('led_ms_cabinet_warranty_terms'),
                commissionedDate: fromR('led_ms_cabinet_commissioned_date'),
                installationPartner: fromR('led_ms_cabinet_installation_partner'),
                installedBy: fromR('led_ms_cabinet_installed_by'),
                installedOn: fromR('led_ms_cabinet_installed_on'),
                associatedPoleId: fromR('led_ms_cabinet_associated_pole'),
                cabinetDepthMm: fromR('led_ms_cabinet_depth'),
                cabinetHeightMm: fromR('led_ms_cabinet_height'),
                cabinetWidthMm: fromR('led_ms_cabinet_width'),
                cabinetType: fromR('led_ms_cabinet_type'),
                corrosionProtection: fromR('led_ms_cabinet_protection'),
            };
        } else if (cat === 'LED Receiving Card') {
            common = {
                id: fromR('led_receiving_card_id', child.id),
                description: fromR('led_receiving_card_description'),
                purchaseDate: fromR('led_receiving_card_purchase_date'),
                purchaseFrom: fromR('led_receiving_card_purchased_from'),
                brand: fromR('led_receiving_card_brand'),
                model: fromR('led_receiving_card_model'),
                vendorContact: fromR('led_receiving_card_vendor_contact_name'),
                vendorEmail: fromR('led_receiving_card_vendor_email'),
                vendorPhone: fromR('led_receiving_card_vendor_phone_number'),
                invoiceNumber: fromR('led_receiving_card_invoice_number'),
                poNumber: fromR('led_receiving_card_po_number'),
                currency: fromR('led_receiving_card_currency'),
                cost: fromR('led_receiving_card_cost'),
                warrantyStart: fromR('led_receiving_card_warranty_start'),
                warrantyEnd: fromR('led_receiving_card_warranty_end'),
                warrantyTerms: fromR('led_receiving_card_warranty_terms'),
                commissionedDate: fromR('led_receiving_card_commissioned_date'),
                installationPartner: fromR('led_receiving_card_installation_partner'),
                installedBy: fromR('led_receiving_card_installed_by'),
                installedOn: fromR('led_receiving_card_installed_on'),
                associatedPoleId: fromR('led_receiving_card_associated_pole'),
            };
        } else if (cat === 'LED SMPS') {
            common = {
                id: fromR('led_smps_id', child.id),
                description: fromR('led_smps_description'),
                purchaseDate: fromR('led_smps_purchase_date'),
                purchaseFrom: fromR('led_smps_purchased_from'),
                brand: fromR('led_smps_brand'),
                model: fromR('led_smps_model'),
                vendorContact: fromR('led_smps_vendor_contact_name'),
                vendorEmail: fromR('led_smps_vendor_email'),
                vendorPhone: fromR('led_smps_vendor_phone_number'),
                invoiceNumber: fromR('led_smps_invoice_number'),
                poNumber: fromR('led_smps_po_number'),
                currency: fromR('led_smps_currency'),
                cost: fromR('led_smps_cost'),
                warrantyStart: fromR('led_smps_warranty_start'),
                warrantyEnd: fromR('led_smps_warranty_end'),
                warrantyTerms: fromR('led_smps_warranty_terms'),
                commissionedDate: fromR('led_smps_commissioned_date'),
                installationPartner: fromR('led_smps_installation_partner'),
                installedBy: fromR('led_smps_installed_by'),
                installedOn: fromR('led_smps_installed_on'),
                associatedPoleId: fromR('led_smps_associated_pole'),
            };
        } else if (cat === 'Solar Battery') {
            common = {
                id: fromR('solar_battery_id', child.id),
                description: fromR('solar_battery_description'),
                purchaseDate: fromR('solar_battery_purchase_date'),
                purchaseFrom: fromR('solar_battery_purchased_from'),
                brand: fromR('solar_battery_brand'),
                model: fromR('solar_battery_model'),
                vendorContact: fromR('solar_battery_vendor_contact_name'),
                vendorEmail: fromR('solar_battery_vendor_email'),
                vendorPhone: fromR('solar_battery_vendor_phone_number'),
                invoiceNumber: fromR('solar_battery_invoice_number'),
                poNumber: fromR('solar_battery_po_number'),
                currency: fromR('solar_battery_currency'),
                cost: fromR('solar_battery_cost'),
                warrantyStart: fromR('solar_battery_warranty_start'),
                warrantyEnd: fromR('solar_battery_warranty_end'),
                warrantyTerms: fromR('solar_battery_warranty_terms'),
                commissionedDate: fromR('solar_battery_commissioned_date'),
                installationPartner: fromR('solar_battery_installation_partner'),
                installedBy: fromR('solar_battery_installed_by'),
                installedOn: fromR('solar_battery_installed_on'),
                associatedPoleId: fromR('solar_battery_associated_pole'),
            };
        } else {
            // Default / Pole - use pole_ prefix
            let locId = r.pole_installation_location ? String(r.pole_installation_location) : '';
            let siteId = '';
            if (locId && locationsList) {
                const loc = locationsList.find(l => String(l.id) === locId);
                if (loc && loc.siteId) {
                    siteId = String(loc.siteId);
                }
            }

            common = {
                id: fromR('pole_id', child.id),
                description: fromR('pole_description'),
                purchaseDate: fromR('pole_purchase_date'),
                purchaseFrom: fromR('pole_purchased_from'),
                brand: fromR('pole_brand'),
                model: fromR('pole_model'),
                vendorContact: fromR('pole_vendor_contact_name'),
                vendorEmail: fromR('pole_vendor_email'),
                vendorPhone: fromR('pole_vendor_phone_number'),
                invoiceNumber: fromR('pole_invoice_number'),
                poNumber: fromR('pole_po_number'),
                currency: fromR('pole_currency'),
                cost: fromR('pole_cost'),
                warrantyStart: fromR('pole_warranty_start'),
                warrantyEnd: fromR('pole_warranty_end'),
                warrantyTerms: fromR('pole_warranty_terms'),
                commissionedDate: fromR('pole_commissioned_date'),
                installationPartner: fromR('pole_installation_partner'),
                installedBy: fromR('pole_installed_by'),
                installedOn: fromR('pole_installed_on'),
                selectSite: siteId,
                installationLocation: locId,
                latitude: fromR('pole_latitude'),
                longitude: fromR('pole_longitude'),
                fan_speed_rpm_cfm: fromR('pole_fan_speed_rpm_cfm') !== '' ? Number(fromR('pole_fan_speed_rpm_cfm')) : (fromR('fan_speed_rpm_cfm') !== '' ? Number(fromR('fan_speed_rpm_cfm')) : ''),
                foundationType: fromR('pole_foundation_type'),
                poleHeight: fromR('pole_height'),
                poleMaterial: fromR('pole_material'),
                windRating: fromR('pole_wind_rating'),
                cameraEnabled: bool2radio(r.pole_camera_enabled),
                displayEnabled: bool2radio(r.pole_display_enabled),
                solarEnabled: bool2radio(r.pole_solar_enabled),
                wifiEnabled: bool2radio(r.pole_wifi_enabled),
                lightingEnabled: bool2radio(r.pole_lighting_enabled),
            };
        }

        setFormData({
            ...EMPTY_FORM,
            autoId: child.id,
            category: matchedCat ? matchedCat.id : '',
            categoryName: matchedCat ? matchedCat.name : cat,
            //: r.serial_no || child.serialNo || '',
            //department: r.department || child.department || '',
            //assignedTo: r.assigned_to || child.assignedTo || '',
            ...common,
        });
        setFormErrors({});
        setViewMode('form');
    };

    // Fetch the child asset's full data from single-record API, then pre-fill the edit form
    const openEditFormFetch = async (child) => {
        const assetType = child.childType || child.category || 'Pole';
        const assetId = child.id;
        const getUrl = ASSET_ENDPOINTS[assetType];
        if (!getUrl) {
            openEditForm(child, {});
            return;
        }
        try {
            const accessToken = localStorage.getItem('accessToken');
            const res = await fetch(getUrl(assetId), {
                headers: accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {},
            });
            const json = await res.json();
            const rawData = json?.data || {};
            openEditForm(child, rawData);
        } catch (err) {
            console.error('[openEditFormFetch] Error:', err);
            openEditForm(child, {});
        }
    };

    const handleDelete = () => {
        const { parentIdx, childId } = pendingDelete;
        setAssets(prev => prev.map((a, i) =>
            i === parentIdx ? { ...a, children: a.children.filter(c => c.id !== childId) } : a
        ));
        setAlert({ type: 'success', message: 'Asset deleted successfully.' });
        setTimeout(() => setAlert(null), 3000);
        setPendingDelete(null);
    };

    // ── Form field change ────────────────────────────────────────────────────
    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
        setFormErrors(prev => ({ ...prev, [e.target.name]: '' }));
    };

    // ── Supplier select change (auto-fill vendor contact name, email, phone) ───
    const handleSupplierChange = (e) => {
        const selectedName = e.target.value;
        const matched = suppliersList.find(s => s.name === selectedName);

        setFormData(prev => ({
            ...prev,
            purchaseFrom: selectedName,
            ...(matched ? {
                vendorContact: matched.name || prev.vendorContact,
                vendorEmail: matched.email || prev.vendorEmail,
                vendorPhone: matched.telephone || matched.phone || prev.vendorPhone,
            } : {})
        }));

        setFormErrors(prev => ({
            ...prev,
            purchaseFrom: '',
            ...(matched?.name ? { vendorContact: '' } : {}),
            ...(matched?.email ? { vendorEmail: '' } : {}),
            ...(matched?.telephone || matched?.phone ? { vendorPhone: '' } : {}),
        }));
    };

    // Auto-format date input as YYYY-MM-DD while user types
    const handleDateChange = (e) => {
        const { name } = e.target;
        let digits = e.target.value.replace(/\D/g, ''); // strip non-digits
        if (digits.length > 8) digits = digits.slice(0, 8);
        let formatted = digits;
        if (digits.length > 4) formatted = digits.slice(0, 4) + '-' + digits.slice(4);
        if (digits.length > 6) formatted = digits.slice(0, 4) + '-' + digits.slice(4, 6) + '-' + digits.slice(6);
        setFormData(prev => ({ ...prev, [name]: formatted }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    // ── Form submit ──────────────────────────────────────────────────────────
    const handleSubmit = async (isDraft = false) => {
        const errors = {};
        if (!formData.category) errors.category = 'Category is required.';

        // Skip detailed validation when editing — form is pre-filled from the API
        if (!isDraft && formData.category && !editingAsset) {
            const requiredCommon = [
                'id', 'description', 'purchaseDate', 'purchaseFrom',
                'brand', 'model', 'vendorContact', 'vendorEmail',
                'vendorPhone', 'invoiceNumber', 'poNumber', 'currency', 'cost',
                'warrantyStart', 'warrantyEnd', 'warrantyTerms',
                'commissionedDate', 'installationPartner', 'installedBy', 'installedOn'
            ];

            requiredCommon.forEach(field => {
                if (!formData[field]) errors[field] = 'This field is required.';
            });

            if (formData.categoryName === 'Pole') {
                const requiredPole = [
                    'selectSite', 'installationLocation', 'latitude', 'longitude', 'fan_speed_rpm_cfm',
                    'foundationType', 'poleHeight', 'poleMaterial', 'windRating',
                    'cameraEnabled', 'displayEnabled', 'solarEnabled', 'wifiEnabled', 'lightingEnabled'
                ];
                requiredPole.forEach(field => {
                    if (!formData[field]) errors[field] = 'This field is required.';
                });
            } else if (formData.categoryName === 'Solar Light') {
                const requiredSolar = [
                    'associatedPoleId', 'autoDimming', 'batteryCapacity', 'beamAngleDegree',
                    'chargeControllerMode', 'colorTemperature', 'lightPosition',
                    'lumens', 'wattage'
                ];
                requiredSolar.forEach(field => {
                    if (!formData[field]) errors[field] = 'This field is required.';
                });
            } else if (formData.categoryName === 'LED Receiving Card') {
                const requiredLedCard = [
                    'associatedPoleId', 'communicationType',
                    'maxResolutionSupported', 'position', 'refreshRateHz',
                    'supportedPixelCapacity', 'wifiEnabled'
                ];
                requiredLedCard.forEach(field => {
                    if (!formData[field]) errors[field] = 'This field is required.';
                });
            } else if (formData.categoryName === 'LED SMPS') {
                const requiredSmps = [
                    'associatedPoleId', 'expectedLifeHours',
                    'position', 'wifiEnabled'
                ];
                requiredSmps.forEach(field => {
                    if (!formData[field]) errors[field] = 'This field is required.';
                });
            } else if (formData.categoryName === 'LED MS Cabinet') {
                const requiredCabinet = [
                    'associatedPoleId', 'cabinetDepthMm',
                    'cabinetHeightMm', 'cabinetType', 'cabinetWidthMm', 'corrosionProtection'
                ];
                requiredCabinet.forEach(field => {
                    if (!formData[field]) errors[field] = 'This field is required.';
                });
            } else if (formData.categoryName === 'Solar Battery') {
                const requiredBattery = [
                    'associatedPoleId', 'managementSystem', 'position',
                    'type', 'capacityAh', 'capacityKwh', 'disposalMethod', 'expectedLifeCycles',
                    'expectedLifeYears', 'maxChargeVoltage', 'maxDischargeCurrent',
                    'nominalVoltage', 'recyclingPartner', 'replacementThreshold'
                ];
                requiredBattery.forEach(field => {
                    if (!formData[field]) errors[field] = 'This field is required.';
                });
            } else if (formData.categoryName === 'LED Controller') {
                const requiredController = [
                    'associatedPoleId', 'cloudConnected',
                    'communicationInterface', 'controlMode', 'controllerModel', 'position',
                    'inputInterfaces', 'maxPixelCapacity', 'outputPorts', 'supportedResolution'
                ];
                requiredController.forEach(field => {
                    if (!formData[field]) errors[field] = 'This field is required.';
                });
            } else if (formData.categoryName === 'Sensor') {
                const requiredSensor = [
                    'associatedPoleId', 'calibrationDate', 'commProtocol', 'firmwareVersion', 'metricsSupported',
                    'sensorOperatingRange', 'reportingFreqSeconds', 'sensorCategory', 'position', 'wifiEnabled'
                ];
                requiredSensor.forEach(field => {
                    if (!formData[field]) errors[field] = 'This field is required.';
                });
            } else if (formData.categoryName === 'Security Camera') {
                const requiredCamera = [
                    'associatedPoleId', 'cameraType', 'cameraConnectivity', 'frameRate',
                    'nightVision', 'cameraResolution', 'retentionDays', 'wifiEnabled'
                ];
                requiredCamera.forEach(field => {
                    if (!formData[field]) errors[field] = 'This field is required.';
                });
            } else if (formData.categoryName === 'Rain Hood') {
                const requiredHood = [
                    'associatedPoleId', 'drainageDesign', 'hoodType', 'hoodMaterial'
                ];
                requiredHood.forEach(field => {
                    if (!formData[field]) errors[field] = 'This field is required.';
                });
            } else if (formData.categoryName === 'Fan') {
                const requiredFan = [
                    'associatedPoleId', 'airFlow', 'operatingTempRange', 'position',
                    'fanPower', 'fanRatedRpm', 'fanRole', 'speedControl', 'type', 'wifiEnabled'
                ];
                requiredFan.forEach(field => {
                    if (!formData[field]) errors[field] = 'This field is required.';
                });
            } else if (formData.categoryName === 'Cartridge') {
                const requiredCartridge = [
                    'associatedPoleId', 'position', 'type', 'disposalMethod',
                    'expectedLifeHours', 'mediaDescription', 'replacementThreshold'
                ];
                requiredCartridge.forEach(field => {
                    if (!formData[field]) errors[field] = 'This field is required.';
                });
            } else if (formData.categoryName === 'LED Screen') {
                const requiredScreen = [
                    'associatedPoleId', 'ledConnectivity', 'ledControllerModel', 'brightnessNits',
                    'position', 'ledRefreshRateHz', 'ledResolution', 'ledSize', 'type'
                ];
                requiredScreen.forEach(field => {
                    if (!formData[field]) errors[field] = 'This field is required.';
                });
            }
        }

        if (Object.keys(errors).length) {
            setFormErrors(errors);
            setAlert({ type: 'error', message: 'Please fill all required fields.' });
            setTimeout(() => setAlert(null), 3000);
            return;
        }

        if (isDraft) {
            setAlert({ type: 'success', message: 'Draft saved successfully!' });
            setTimeout(() => setAlert(null), 3000);
            setViewMode('list');
            return;
        }

        // ── API Calls by Category ─────────────────────────────────────────────
        try {
            const accessToken = localStorage.getItem('accessToken');
            const headers = {
                'Content-Type': 'application/json',
                ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
            };

            // ── UPDATE (Edit) flow — PUT to update endpoint ───────────────────────
            if (editingAsset) {
                if (formData.categoryName === 'Pole') {
                    const poleId = editingAsset.id;
                    const payload = {
                        pole_id: formData.id,
                        category: Number(formData.category),
                        pole_description: formData.description,
                        pole_purchase_date: formData.purchaseDate,
                        pole_purchased_from: formData.purchaseFrom,
                        pole_brand: formData.brand,
                        pole_model: formData.model,
                        pole_vendor_contact_name: formData.vendorContact,
                        pole_vendor_email: formData.vendorEmail,
                        pole_vendor_phone_number: formData.vendorPhone,
                        pole_invoice_number: formData.invoiceNumber,
                        pole_po_number: formData.poNumber,
                        pole_currency: formData.currency,
                        pole_cost: formData.cost,
                        pole_warranty_start: formData.warrantyStart,
                        pole_warranty_end: formData.warrantyEnd,
                        pole_warranty_terms: formData.warrantyTerms,
                        pole_commissioned_date: formData.commissionedDate,
                        pole_installed_by: formData.installedBy,
                        pole_installed_on: formData.installedOn,
                        pole_installation_partner: formData.installationPartner,
                        pole_installation_location: formData.installationLocation ? Number(formData.installationLocation) : undefined,
                        pole_latitude: formData.latitude,
                        pole_longitude: formData.longitude,
                        pole_fan_speed_rpm_cfm: Number(formData.fan_speed_rpm_cfm),
                        fan_speed_rpm_cfm: Number(formData.fan_speed_rpm_cfm),
                        pole_foundation_type: formData.foundationType,
                        pole_height: formData.poleHeight,
                        pole_material: formData.poleMaterial,
                        pole_wind_rating: formData.windRating,
                        pole_camera_enabled: formData.cameraEnabled === 'Yes',
                        pole_display_enabled: formData.displayEnabled === 'Yes',
                        pole_solar_enabled: formData.solarEnabled === 'Yes',
                        pole_wifi_enabled: formData.wifiEnabled === 'Yes',
                        pole_lighting_enabled: formData.lightingEnabled === 'Yes',
                        last_updated_by: user?.username || user?.user_id || user?.id
                    };
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/poles/update/${poleId}/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                    setAlert({ type: 'success', message: 'Pole updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                    setEditingAsset(null); setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();
                    return;
                } else if (formData.categoryName === 'Sensor') {

                    const sensorId = editingAsset.id;
                    const payload = {
                        sensor_id: formData.id,
                        sensor_description: formData.description,
                        sensor_purchase_date: formData.purchaseDate,
                        sensor_purchased_from: formData.purchaseFrom,
                        sensor_brand: formData.brand,
                        sensor_model: formData.model,
                        sensor_vendor_contact_name: formData.vendorContact,
                        sensor_vendor_email: formData.vendorEmail,
                        sensor_vendor_phone_number: formData.vendorPhone,
                        sensor_invoice_number: formData.invoiceNumber,
                        sensor_po_number: formData.poNumber,
                        sensor_currency: formData.currency,
                        sensor_cost: formData.cost,
                        sensor_warranty_start: formData.warrantyStart,
                        sensor_warranty_end: formData.warrantyEnd,
                        sensor_warranty_terms: formData.warrantyTerms,
                        sensor_commissioned_date: formData.commissionedDate,
                        sensor_installed_by: formData.installedBy,
                        sensor_installed_on: formData.installedOn,
                        sensor_installation_partner: formData.installationPartner,
                        sensor_calibration_date: formData.calibrationDate,
                        sensor_communication_protocol: formData.commProtocol,
                        sensor_firmware_version: formData.firmwareVersion,
                        sensor_metrics_supported: formData.metricsSupported,
                        sensor_operating_range: formData.sensorOperatingRange,
                        sensor_reporting_frequency_seconds: formData.reportingFreqSeconds ? Number(formData.reportingFreqSeconds) : undefined,
                        sensor_category: formData.sensorCategory,
                        sensor_position: formData.position,
                        sensor_wifi_enabled: formData.wifiEnabled === 'Yes',
                        sensor_associated_pole: formData.associatedPoleId,
                        last_updated_by: user?.username || user?.user_id || user?.id
                    };
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sensor/update/${sensorId}/`, {
                        method: 'POST', headers, body: JSON.stringify(payload),

                    });
                    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                    setAlert({ type: 'success', message: 'Sensor updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                    setEditingAsset(null);
                    setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();
                    return;
                } else if (formData.categoryName === 'Solar Light') {

                    const id = editingAsset.id;
                    const payload = {
                        solar_light_id: formData.id,
                        solar_light_description: formData.description,
                        solar_light_purchase_date: formData.purchaseDate,
                        solar_light_purchased_from: formData.purchaseFrom,
                        solar_light_brand: formData.brand,
                        solar_light_model: formData.model,
                        solar_light_vendor_contact_name: formData.vendorContact,
                        solar_light_vendor_email: formData.vendorEmail,
                        solar_light_vendor_phone_number: formData.vendorPhone,
                        solar_light_invoice_number: formData.invoiceNumber,
                        solar_light_po_number: formData.poNumber,
                        solar_light_currency: formData.currency,
                        solar_light_cost: formData.cost,
                        solar_light_warranty_start: formData.warrantyStart,
                        solar_light_warranty_end: formData.warrantyEnd,
                        solar_light_warranty_terms: formData.warrantyTerms,
                        solar_light_commissioned_date: formData.commissionedDate,
                        solar_light_installed_by: formData.installedBy,
                        solar_light_installed_on: formData.installedOn,
                        solar_light_installation_partner: formData.installationPartner,
                        solar_light_auto_dimming: formData.autoDimming === 'Yes',
                        solar_light_battery_capacity: formData.batteryCapacity,
                        solar_light_beam_angle_degree: formData.beamAngleDegree,
                        solar_light_charge_controller_mode: formData.chargeControllerMode === 'Yes',
                        solar_light_color_temperature: formData.colorTemperature,
                        solar_light_light_position: formData.lightPosition,
                        solar_light_lumens: formData.lumens,
                        solar_light_wattage: formData.wattage,
                        solar_associated_pole: formData.associatedPoleId,
                        last_updated_by: user?.username || user?.user_id || user?.id
                    };
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/solar-lights/update/${id}/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                    setAlert({ type: 'success', message: 'Solar Light updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                    setEditingAsset(null); setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();
                    return;

                } else if (formData.categoryName === 'Security Camera') {
                    const id = editingAsset.id;
                    const payload = {
                        security_camera_id: formData.id,
                        security_camera_description: formData.description,
                        security_camera_purchase_date: formData.purchaseDate,
                        security_camera_purchased_from: formData.purchaseFrom,
                        security_camera_brand: formData.brand,
                        security_camera_model: formData.model,
                        security_camera_vendor_contact_name: formData.vendorContact,
                        security_camera_vendor_email: formData.vendorEmail,
                        security_camera_vendor_phone_number: formData.vendorPhone,
                        security_camera_invoice_number: formData.invoiceNumber,
                        security_camera_po_number: formData.poNumber,
                        security_camera_currency: formData.currency,
                        security_camera_cost: formData.cost,
                        security_camera_warranty_start: formData.warrantyStart,
                        security_camera_warranty_end: formData.warrantyEnd,
                        security_camera_warranty_terms: formData.warrantyTerms,
                        security_camera_commissioned_date: formData.commissionedDate,
                        security_camera_installed_by: formData.installedBy,
                        security_camera_installed_on: formData.installedOn,
                        security_camera_installation_partner: formData.installationPartner,
                        security_camera_camera_type: formData.cameraType,
                        security_camera_connectivity: formData.cameraConnectivity,
                        security_camera_frame_rate: formData.frameRate,
                        security_camera_night_vision: formData.nightVision === 'Yes',
                        security_camera_resolution: formData.cameraResolution,
                        security_camera_retention_days: formData.retentionDays,
                        security_wifi_enabled: formData.wifiEnabled === 'Yes',
                        security_camera_associated_pole: formData.associatedPoleId,
                        last_updated_by: user?.username || user?.user_id || user?.id
                    };
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/security-cameras/update/${id}/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                    setAlert({ type: 'success', message: 'Security Camera updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                    setEditingAsset(null); setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();
                    return;

                } else if (formData.categoryName === 'Rain Hood') {
                    const id = editingAsset.id;
                    const payload = {
                        rain_hood_id: formData.id,
                        rain_hood_description: formData.description,
                        rain_hood_purchase_date: formData.purchaseDate,
                        rain_hood_purchased_from: formData.purchaseFrom,
                        rain_hood_brand: formData.brand,
                        rain_hood_model: formData.model,
                        rain_hood_vendor_contact_name: formData.vendorContact,
                        rain_hood_vendor_email: formData.vendorEmail,
                        rain_hood_vendor_phone_number: formData.vendorPhone,
                        rain_hood_invoice_number: formData.invoiceNumber,
                        rain_hood_po_number: formData.poNumber,
                        rain_hood_currency: formData.currency,
                        rain_hood_cost: formData.cost,
                        rain_hood_warranty_start: formData.warrantyStart,
                        rain_hood_warranty_end: formData.warrantyEnd,
                        rain_hood_warranty_terms: formData.warrantyTerms,
                        rain_hood_commissioned_date: formData.commissionedDate,
                        rain_hood_installed_by: formData.installedBy,
                        rain_hood_installed_on: formData.installedOn,
                        rain_hood_installation_partner: formData.installationPartner,
                        rain_hood_drainage_design: formData.drainageDesign,
                        rain_hood_hood_type: formData.hoodType,
                        rain_hood_material: formData.hoodMaterial,
                        rain_hood_associated_pole: formData.associatedPoleId,
                        last_updated_by: user?.username || user?.user_id || user?.id
                    };
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/rainhood/update/${id}/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                    setAlert({ type: 'success', message: 'Rain Hood updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                    setEditingAsset(null); setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();
                    return;

                } else if (formData.categoryName === 'Fan') {
                    const id = editingAsset.id;
                    const payload = {
                        fan_id: formData.id,
                        fan_description: formData.description,
                        fan_purchase_date: formData.purchaseDate,
                        fan_purchased_from: formData.purchaseFrom,
                        fan_brand: formData.brand,
                        fan_model: formData.model,
                        fan_vendor_contact_name: formData.vendorContact,
                        fan_vendor_email: formData.vendorEmail,
                        fan_vendor_phone_number: formData.vendorPhone,
                        fan_invoice_number: formData.invoiceNumber,
                        fan_po_number: formData.poNumber,
                        fan_currency: formData.currency,
                        fan_cost: formData.cost,
                        fan_warranty_start: formData.warrantyStart,
                        fan_warranty_end: formData.warrantyEnd,
                        fan_warranty_terms: formData.warrantyTerms,
                        fan_commissioned_date: formData.commissionedDate,
                        fan_installed_by: formData.installedBy,
                        fan_installed_on: formData.installedOn,
                        fan_installation_partner: formData.installationPartner,
                        fan_air_flow: formData.airFlow,
                        fan_operating_temperature_range: formData.operatingTempRange,
                        fan_position: formData.position,
                        fan_power: formData.fanPower,
                        fan_rated_rpm: formData.fanRatedRpm ? Number(formData.fanRatedRpm) : undefined,
                        fan_role: formData.fanRole,
                        fan_speed_control: formData.speedControl,
                        fan_type: formData.type,
                        fan_wifi_enabled: formData.wifiEnabled === 'Yes',
                        fan_associated_pole: formData.associatedPoleId,
                        last_updated_by: user?.username || user?.user_id || user?.id
                    };
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/fan/update/${id}/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                    setAlert({ type: 'success', message: 'Fan updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                    setEditingAsset(null); setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();
                    return;

                } else if (formData.categoryName === 'Cartridge') {
                    const id = editingAsset.id;
                    const payload = {
                        cartridge_id: formData.id,
                        cartridge_description: formData.description,
                        cartridge_purchase_date: formData.purchaseDate,
                        cartridge_purchased_from: formData.purchaseFrom,
                        cartridge_brand: formData.brand,
                        cartridge_model: formData.model,
                        cartridge_vendor_contact_name: formData.vendorContact,
                        cartridge_vendor_email: formData.vendorEmail,
                        cartridge_vendor_phone_number: formData.vendorPhone,
                        cartridge_invoice_number: formData.invoiceNumber,
                        cartridge_po_number: formData.poNumber,
                        cartridge_currency: formData.currency,
                        cartridge_cost: formData.cost,
                        cartridge_warranty_start: formData.warrantyStart,
                        cartridge_warranty_end: formData.warrantyEnd,
                        cartridge_warranty_terms: formData.warrantyTerms,
                        cartridge_commissioned_date: formData.commissionedDate,
                        cartridge_installed_by: formData.installedBy,
                        cartridge_installed_on: formData.installedOn,
                        cartridge_installation_partner: formData.installationPartner,
                        cartridge_position: formData.position,
                        cartridge_type: formData.type,
                        cartridge_disposal_method: formData.disposalMethod,
                        cartridge_expected_life_hours: formData.expectedLifeHours ? Number(formData.expectedLifeHours) : undefined,
                        cartridge_media_description: formData.mediaDescription,
                        cartridge_replacement_threshold: formData.replacementThreshold,
                        cartridge_associated_pole: formData.associatedPoleId,
                        last_updated_by: user?.username || user?.user_id || user?.id
                    };
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cartridge/update/${id}/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                    setAlert({ type: 'success', message: 'Cartridge updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                    setEditingAsset(null); setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();
                    return;

                } else if (formData.categoryName === 'LED Screen') {
                    const id = editingAsset.id;
                    const payload = {
                        led_screen_id: formData.id,
                        led_screen_description: formData.description,
                        led_screen_purchase_date: formData.purchaseDate,
                        led_screen_purchased_from: formData.purchaseFrom,
                        led_screen_brand: formData.brand,
                        led_screen_model: formData.model,
                        led_screen_vendor_contact_name: formData.vendorContact,
                        led_screen_vendor_email: formData.vendorEmail,
                        led_screen_vendor_phone_number: formData.vendorPhone,
                        led_screen_invoice_number: formData.invoiceNumber,
                        led_screen_po_number: formData.poNumber,
                        led_screen_currency: formData.currency,
                        led_screen_cost: formData.cost,
                        led_screen_warranty_start: formData.warrantyStart,
                        led_screen_warranty_end: formData.warrantyEnd,
                        led_screen_warranty_terms: formData.warrantyTerms,
                        led_screen_commissioned_date: formData.commissionedDate,
                        led_screen_installed_by: formData.installedBy,
                        led_screen_installed_on: formData.installedOn,
                        led_screen_installation_partner: formData.installationPartner,
                        led_screen_connectivity: formData.ledConnectivity,
                        led_screen_controller_model: formData.ledControllerModel,
                        led_screen_brightness_nits: formData.brightnessNits ? Number(formData.brightnessNits) : undefined,
                        led_screen_position: formData.position,
                        led_screen_refresh_rate_hz: formData.ledRefreshRateHz ? Number(formData.ledRefreshRateHz) : undefined,
                        led_screen_resolution: formData.ledResolution,
                        led_screen_size: formData.ledSize,
                        led_screen_type: formData.type,
                        led_screen_associated_pole: formData.associatedPoleId,
                        last_updated_by: user?.username || user?.user_id || user?.id
                    };
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/led-screen/update/${id}/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                    setAlert({ type: 'success', message: 'LED Screen updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                    setEditingAsset(null); setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();
                    return;

                } else if (formData.categoryName === 'LED Controller') {
                    const id = editingAsset.id;
                    const payload = {
                        led_controller_id: formData.id,
                        led_controller_description: formData.description,
                        led_controller_purchase_date: formData.purchaseDate,
                        led_controller_purchased_from: formData.purchaseFrom,
                        led_controller_brand: formData.brand,
                        led_controller_model: formData.model,
                        led_controller_vendor_contact_name: formData.vendorContact,
                        led_controller_vendor_email: formData.vendorEmail,
                        led_controller_vendor_phone_number: formData.vendorPhone,
                        led_controller_invoice_number: formData.invoiceNumber,
                        led_controller_po_number: formData.poNumber,
                        led_controller_currency: formData.currency,
                        led_controller_cost: formData.cost,
                        led_controller_warranty_start: formData.warrantyStart,
                        led_controller_warranty_end: formData.warrantyEnd,
                        led_controller_warranty_terms: formData.warrantyTerms,
                        led_controller_commissioned_date: formData.commissionedDate,
                        led_controller_installed_by: formData.installedBy,
                        led_controller_installed_on: formData.installedOn,
                        led_controller_installation_partner: formData.installationPartner,
                        led_controller_cloud_connected: formData.cloudConnected === 'Yes',
                        led_controller_communication_interface: formData.communicationInterface,
                        led_controller_control_mode: formData.controlMode,
                        led_controller_position: formData.position,
                        led_controller_input_interfaces: formData.inputInterfaces,
                        led_controller_max_pixel_capacity: formData.maxPixelCapacity ? Number(formData.maxPixelCapacity) : undefined,
                        led_controller_output_ports: formData.outputPorts ? Number(formData.outputPorts) : undefined,
                        led_controller_supported_resolution: formData.supportedResolution,
                        led_controller_associated_pole: formData.associatedPoleId,
                        last_updated_by: user?.username || user?.user_id || user?.id
                    };
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/led-controller/update/${id}/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                    setAlert({ type: 'success', message: 'LED Controller updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                    setEditingAsset(null); setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();
                    return;

                } else if (formData.categoryName === 'LED MS Cabinet') {
                    const id = editingAsset.id;
                    const payload = {
                        led_ms_cabinet_id: formData.id,
                        led_ms_cabinet_description: formData.description,
                        led_ms_cabinet_purchase_date: formData.purchaseDate,
                        led_ms_cabinet_purchased_from: formData.purchaseFrom,
                        led_ms_cabinet_brand: formData.brand,
                        led_ms_cabinet_model: formData.model,
                        led_ms_cabinet_vendor_contact_name: formData.vendorContact,
                        led_ms_cabinet_vendor_email: formData.vendorEmail,
                        led_ms_cabinet_vendor_phone_number: formData.vendorPhone,
                        led_ms_cabinet_invoice_number: formData.invoiceNumber,
                        led_ms_cabinet_po_number: formData.poNumber,
                        led_ms_cabinet_currency: formData.currency,
                        led_ms_cabinet_cost: formData.cost,
                        led_ms_cabinet_warranty_start: formData.warrantyStart,
                        led_ms_cabinet_warranty_end: formData.warrantyEnd,
                        led_ms_cabinet_warranty_terms: formData.warrantyTerms,
                        led_ms_cabinet_commissioned_date: formData.commissionedDate,
                        led_ms_cabinet_installed_by: formData.installedBy,
                        led_ms_cabinet_installed_on: formData.installedOn,
                        led_ms_cabinet_installation_partner: formData.installationPartner,
                        led_ms_cabinet_depth: formData.cabinetDepthMm,
                        led_ms_cabinet_height: formData.cabinetHeightMm,
                        led_ms_cabinet_width: formData.cabinetWidthMm,
                        led_ms_cabinet_type: formData.cabinetType,
                        led_ms_cabinet_protection: formData.corrosionProtection,
                        led_ms_cabinet_associated_pole: formData.associatedPoleId,
                        last_updated_by: user?.username || user?.user_id || user?.id
                    };
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/led-ms-cabinet/update/${id}/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                    setAlert({ type: 'success', message: 'LED MS Cabinet updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                    setEditingAsset(null); setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();
                    return;

                } else if (formData.categoryName === 'LED Receiving Card') {
                    const id = editingAsset.id;
                    const payload = {
                        led_receiving_card_id: formData.id,
                        led_receiving_card_description: formData.description,
                        led_receiving_card_purchase_date: formData.purchaseDate,
                        led_receiving_card_purchased_from: formData.purchaseFrom,
                        led_receiving_card_brand: formData.brand,
                        led_receiving_card_model: formData.model,
                        led_receiving_card_vendor_contact_name: formData.vendorContact,
                        led_receiving_card_vendor_email: formData.vendorEmail,
                        led_receiving_card_vendor_phone_number: formData.vendorPhone,
                        led_receiving_card_invoice_number: formData.invoiceNumber,
                        led_receiving_card_po_number: formData.poNumber,
                        led_receiving_card_currency: formData.currency,
                        led_receiving_card_cost: formData.cost,
                        led_receiving_card_warranty_start: formData.warrantyStart,
                        led_receiving_card_warranty_end: formData.warrantyEnd,
                        led_receiving_card_warranty_terms: formData.warrantyTerms,
                        led_receiving_card_commissioned_date: formData.commissionedDate,
                        led_receiving_card_installed_by: formData.installedBy,
                        led_receiving_card_installed_on: formData.installedOn,
                        led_receiving_card_installation_partner: formData.installationPartner,
                        led_receiving_card_associated_pole: formData.associatedPoleId,
                        last_updated_by: user?.username || user?.user_id || user?.id
                    };
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/led-receiving-card/update/${id}/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                    setAlert({ type: 'success', message: 'LED Receiving Card updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                    setEditingAsset(null); setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();
                    return;

                } else if (formData.categoryName === 'LED SMPS') {
                    const id = editingAsset.id;
                    const payload = {
                        led_smps_id: formData.id,
                        led_smps_description: formData.description,
                        led_smps_purchase_date: formData.purchaseDate,
                        led_smps_purchased_from: formData.purchaseFrom,
                        led_smps_brand: formData.brand,
                        led_smps_model: formData.model,
                        led_smps_vendor_contact_name: formData.vendorContact,
                        led_smps_vendor_email: formData.vendorEmail,
                        led_smps_vendor_phone_number: formData.vendorPhone,
                        led_smps_invoice_number: formData.invoiceNumber,
                        led_smps_po_number: formData.poNumber,
                        led_smps_currency: formData.currency,
                        led_smps_cost: formData.cost,
                        led_smps_warranty_start: formData.warrantyStart,
                        led_smps_warranty_end: formData.warrantyEnd,
                        led_smps_warranty_terms: formData.warrantyTerms,
                        led_smps_commissioned_date: formData.commissionedDate,
                        led_smps_installed_by: formData.installedBy,
                        led_smps_installed_on: formData.installedOn,
                        led_smps_installation_partner: formData.installationPartner,
                        led_smps_associated_pole: formData.associatedPoleId,
                        last_updated_by: user?.username || user?.user_id || user?.id
                    };
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/led-smps/update/${id}/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                    setAlert({ type: 'success', message: 'LED SMPS updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                    setEditingAsset(null); setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();
                    return;

                } else if (formData.categoryName === 'Solar Battery') {
                    const id = editingAsset.id;
                    const payload = {
                        solar_battery_id: formData.id,
                        solar_battery_description: formData.description,
                        solar_battery_purchase_date: formData.purchaseDate,
                        solar_battery_purchased_from: formData.purchaseFrom,
                        solar_battery_brand: formData.brand,
                        solar_battery_model: formData.model,
                        solar_battery_vendor_contact_name: formData.vendorContact,
                        solar_battery_vendor_email: formData.vendorEmail,
                        solar_battery_vendor_phone_number: formData.vendorPhone,
                        solar_battery_invoice_number: formData.invoiceNumber,
                        solar_battery_po_number: formData.poNumber,
                        solar_battery_currency: formData.currency,
                        solar_battery_cost: formData.cost,
                        solar_battery_warranty_start: formData.warrantyStart,
                        solar_battery_warranty_end: formData.warrantyEnd,
                        solar_battery_warranty_terms: formData.warrantyTerms,
                        solar_battery_commissioned_date: formData.commissionedDate,
                        solar_battery_installed_by: formData.installedBy,
                        solar_battery_installed_on: formData.installedOn,
                        solar_battery_installation_partner: formData.installationPartner,
                        solar_battery_associated_pole: formData.associatedPoleId,
                        last_updated_by: user?.username || user?.user_id || user?.id
                    };
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/solar-battery/update/${id}/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                    setAlert({ type: 'success', message: 'Solar Battery updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                    setEditingAsset(null); setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();
                    return;
                }
                // No update branch matched
                setAlert({ type: 'info', message: 'Update not yet implemented for this category.' });
                setTimeout(() => setAlert(null), 3000);
                return;
            }

            // ── CREATE flow ────────────────────────────────────────────────────────
            if (formData.categoryName === 'Pole') {
                const payload = {
                    pole_id: formData.id,
                    category: Number(formData.category),
                    pole_description: formData.description,
                    pole_purchase_date: formData.purchaseDate,
                    pole_purchased_from: formData.purchaseFrom,
                    pole_brand: formData.brand,
                    pole_model: formData.model,
                    pole_vendor_contact_name: formData.vendorContact,
                    pole_vendor_email: formData.vendorEmail,
                    pole_vendor_phone_number: formData.vendorPhone,
                    pole_invoice_number: formData.invoiceNumber,
                    pole_po_number: formData.poNumber,
                    pole_currency: formData.currency,
                    pole_cost: formData.cost,
                    pole_warranty_start: formData.warrantyStart,
                    pole_warranty_end: formData.warrantyEnd,
                    pole_warranty_terms: formData.warrantyTerms,
                    pole_commissioned_date: formData.commissionedDate,
                    pole_installation_location: Number(formData.installationLocation),
                    pole_installed_by: formData.installedBy,
                    pole_installed_on: formData.installedOn,
                    pole_installation_partner: formData.installationPartner,
                    pole_latitude: formData.latitude,
                    pole_longitude: formData.longitude,
                    pole_fan_speed_rpm_cfm: Number(formData.fan_speed_rpm_cfm),
                    fan_speed_rpm_cfm: Number(formData.fan_speed_rpm_cfm),
                    pole_foundation_type: formData.foundationType,
                    pole_height: formData.poleHeight,
                    pole_material: formData.poleMaterial,
                    pole_wind_rating: formData.windRating,
                    pole_camera_enabled: formData.cameraEnabled === 'Yes',
                    pole_display_enabled: formData.displayEnabled === 'Yes',
                    pole_solar_enabled: formData.solarEnabled === 'Yes',
                    pole_wifi_enabled: formData.wifiEnabled === 'Yes',
                    pole_lighting_enabled: formData.lightingEnabled === 'Yes',
                    created_by: user?.username || user?.user_id || user?.id,
                    status_id: 1,
                };

                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/poles/create/`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(payload),
                });

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    console.error('[Pole API] Error response →', errData);
                    // API returns { status: false, errors: { field: ["msg", ...] } }
                    let detail = errData.detail || errData.message;
                    if (!detail && errData.errors && typeof errData.errors === 'object') {
                        detail = Object.entries(errData.errors)
                            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : JSON.stringify(v)}`)
                            .join(' | ');
                    }
                    if (!detail) {
                        detail = JSON.stringify(errData);
                    }
                    throw new Error(`${detail}`);
                }

                setAlert({ type: 'success', message: 'Pole asset created successfully!' });
                setTimeout(() => setAlert(null), 3000);
                setViewMode('list');
                setFormData(EMPTY_FORM);
                await fetchAssets(); // refresh the list

            } else if (formData.categoryName === 'Solar Light') {
                const payload = {
                    solar_light_id: formData.id,
                    solar_light_description: formData.description,
                    solar_light_purchase_date: formData.purchaseDate,
                    solar_light_purchased_from: formData.purchaseFrom,
                    solar_light_brand: formData.brand,
                    solar_light_model: formData.model,
                    solar_light_vendor_contact_name: formData.vendorContact,
                    solar_light_vendor_email: formData.vendorEmail,
                    solar_light_vendor_phone_number: formData.vendorPhone,
                    solar_light_invoice_number: formData.invoiceNumber,
                    solar_light_po_number: formData.poNumber,
                    solar_light_currency: formData.currency,
                    solar_light_cost: formData.cost,
                    solar_light_warranty_start: formData.warrantyStart,
                    solar_light_warranty_end: formData.warrantyEnd,
                    solar_light_warranty_terms: formData.warrantyTerms,
                    solar_light_commissioned_date: formData.commissionedDate,
                    solar_light_installed_by: formData.installedBy,
                    solar_light_installed_on: formData.installedOn,
                    solar_light_installation_partner: formData.installationPartner,
                    solar_light_auto_dimming: formData.autoDimming === 'Yes',
                    solar_light_battery_capacity: formData.batteryCapacity,
                    solar_light_beam_angle_degree: formData.beamAngleDegree,
                    solar_light_charge_controller_mode: formData.chargeControllerMode === 'Yes',
                    solar_light_color_temperature: formData.colorTemperature,
                    solar_light_light_position: formData.lightPosition,
                    solar_light_lumens: formData.lumens,
                    solar_light_wattage: formData.wattage,
                    solar_associated_pole: formData.associatedPoleId,
                };

                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/solar-lights/create/`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(payload),
                });

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    console.error('[Solar Light API] Error →', errData);
                    let detail = errData.detail || errData.message;
                    if (!detail && errData.errors && typeof errData.errors === 'object') {
                        detail = Object.entries(errData.errors)
                            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : JSON.stringify(v)}`)
                            .join(' | ');
                    }
                    if (!detail) detail = JSON.stringify(errData);
                    throw new Error(`${detail}`);
                }

                setAlert({ type: 'success', message: 'Solar Light asset created successfully!' });
                setTimeout(() => setAlert(null), 3000);
                setViewMode('list');
                setFormData(EMPTY_FORM);
                await fetchAssets(); // refresh the list

            } else if (formData.categoryName === 'Rain Hood') {
                const payload = {
                    rain_hood_id: formData.id,
                    rain_hood_description: formData.description,
                    rain_hood_purchase_date: formData.purchaseDate,
                    rain_hood_purchased_from: formData.purchaseFrom,
                    rain_hood_brand: formData.brand,
                    rain_hood_model: formData.model,
                    rain_hood_vendor_contact_name: formData.vendorContact,
                    rain_hood_vendor_email: formData.vendorEmail,
                    rain_hood_vendor_phone_number: formData.vendorPhone,
                    rain_hood_invoice_number: formData.invoiceNumber,
                    rain_hood_po_number: formData.poNumber,
                    rain_hood_currency: formData.currency,
                    rain_hood_cost: formData.cost,
                    rain_hood_warranty_start: formData.warrantyStart,
                    rain_hood_warranty_end: formData.warrantyEnd,
                    rain_hood_warranty_terms: formData.warrantyTerms,
                    rain_hood_commissioned_date: formData.commissionedDate,
                    rain_hood_installed_by: formData.installedBy,
                    rain_hood_installed_on: formData.installedOn,
                    rain_hood_installation_partner: formData.installationPartner,
                    rain_hood_drainage_design: formData.drainageDesign,
                    rain_hood_hood_type: formData.hoodType,
                    rain_hood_material: formData.hoodMaterial,
                    rain_hood_associated_pole: formData.associatedPoleId,
                    //created_by: user?.username || user?.user_id || user?.id,
                    created_by: user?.username || user?.user_id || user?.id,
                    //user_id
                };

                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/rainhood/create/`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(payload),
                });

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    console.error('[Rain Hood API] Error →', errData);
                    let detail = errData.detail || errData.message;
                    if (!detail && errData.errors && typeof errData.errors === 'object') {
                        detail = Object.entries(errData.errors)
                            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : JSON.stringify(v)}`)
                            .join(' | ');
                    }
                    if (!detail) detail = JSON.stringify(errData);
                    throw new Error(`${detail}`);
                }

                setAlert({ type: 'success', message: 'Rain Hood asset created successfully!' });
                setTimeout(() => setAlert(null), 3000);
                setViewMode('list');
                setFormData(EMPTY_FORM);
                await fetchAssets();

            } else if (formData.categoryName === 'Fan') {
                const payload = {
                    fan_id: formData.id,
                    fan_description: formData.description,
                    fan_purchase_date: formData.purchaseDate,
                    fan_purchased_from: formData.purchaseFrom,
                    fan_brand: formData.brand,
                    fan_model: formData.model,
                    fan_vendor_contact_name: formData.vendorContact,
                    fan_vendor_email: formData.vendorEmail,
                    fan_vendor_phone_number: formData.vendorPhone,
                    fan_invoice_number: formData.invoiceNumber,
                    fan_po_number: formData.poNumber,
                    fan_currency: formData.currency,
                    fan_cost: formData.cost,
                    fan_warranty_start: formData.warrantyStart,
                    fan_warranty_end: formData.warrantyEnd,
                    fan_warranty_terms: formData.warrantyTerms,
                    fan_commissioned_date: formData.commissionedDate,
                    fan_installed_by: formData.installedBy,
                    fan_installed_on: formData.installedOn,
                    fan_installation_partner: formData.installationPartner,
                    fan_air_flow: formData.airFlow,
                    fan_operating_temperature_range: formData.operatingTempRange,
                    fan_position: formData.position,
                    fan_power: formData.fanPower,
                    fan_rated_rpm: formData.fanRatedRpm ? Number(formData.fanRatedRpm) : undefined,
                    fan_role: formData.fanRole,
                    fan_speed_control: formData.speedControl,
                    fan_type: formData.type,
                    fan_wifi_enabled: formData.wifiEnabled === 'Yes',
                    fan_associated_pole: formData.associatedPoleId,
                    created_by: user?.username || user?.user_id || user?.id,
                };
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/fan/create/`, {
                    method: 'POST', headers, body: JSON.stringify(payload),
                });
                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    let detail = errData.detail || errData.message;
                    if (!detail && errData.errors) detail = Object.entries(errData.errors).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ');
                    throw new Error(detail || JSON.stringify(errData));
                }
                setAlert({ type: 'success', message: 'Fan asset created successfully!' });
                setTimeout(() => setAlert(null), 3000);
                setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();

            } else if (formData.categoryName === 'Security Camera') {
                const payload = {
                    security_camera_id: formData.id,
                    security_camera_description: formData.description,
                    security_camera_purchase_date: formData.purchaseDate,
                    security_camera_purchased_from: formData.purchaseFrom,
                    security_camera_brand: formData.brand,
                    security_camera_model: formData.model,
                    security_camera_vendor_contact_name: formData.vendorContact,
                    security_camera_vendor_email: formData.vendorEmail,
                    security_camera_vendor_phone_number: formData.vendorPhone,
                    security_camera_invoice_number: formData.invoiceNumber,
                    security_camera_po_number: formData.poNumber,
                    security_camera_currency: formData.currency,
                    security_camera_cost: formData.cost,
                    security_camera_warranty_start: formData.warrantyStart,
                    security_camera_warranty_end: formData.warrantyEnd,
                    security_camera_warranty_terms: formData.warrantyTerms,
                    security_camera_commissioned_date: formData.commissionedDate,
                    security_camera_installed_by: formData.installedBy,
                    security_camera_installed_on: formData.installedOn,
                    security_camera_installation_partner: formData.installationPartner,
                    security_camera_camera_type: formData.cameraType,
                    security_camera_connectivity: formData.cameraConnectivity,
                    security_camera_frame_rate: formData.frameRate,
                    security_camera_night_vision: formData.nightVision === 'Yes',
                    security_camera_resolution: formData.cameraResolution,
                    security_camera_retention_days: formData.retentionDays,
                    security_wifi_enabled: formData.wifiEnabled === 'Yes',
                    security_camera_associated_pole: formData.associatedPoleId,
                    created_by: user?.username || user?.user_id || user?.id,
                };
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/security-cameras/create/`, {
                    method: 'POST', headers, body: JSON.stringify(payload),
                });
                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    let detail = errData.detail || errData.message;
                    if (!detail && errData.errors) detail = Object.entries(errData.errors).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ');
                    throw new Error(detail || JSON.stringify(errData));
                }
                setAlert({ type: 'success', message: 'Security Camera asset created successfully!' });
                setTimeout(() => setAlert(null), 3000);
                setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();

            } else if (formData.categoryName === 'Sensor') {
                const payload = {
                    sensor_id: formData.id,
                    sensor_description: formData.description,
                    sensor_purchase_date: formData.purchaseDate,
                    sensor_purchased_from: formData.purchaseFrom,
                    sensor_brand: formData.brand,
                    sensor_model: formData.model,
                    sensor_vendor_contact_name: formData.vendorContact,
                    sensor_vendor_email: formData.vendorEmail,
                    sensor_vendor_phone_number: formData.vendorPhone,
                    sensor_invoice_number: formData.invoiceNumber,
                    sensor_po_number: formData.poNumber,
                    sensor_currency: formData.currency,
                    sensor_cost: formData.cost,
                    sensor_warranty_start: formData.warrantyStart,
                    sensor_warranty_end: formData.warrantyEnd,
                    sensor_warranty_terms: formData.warrantyTerms,
                    sensor_commissioned_date: formData.commissionedDate,
                    sensor_installed_by: formData.installedBy,
                    sensor_installed_on: formData.installedOn,
                    sensor_installation_partner: formData.installationPartner,
                    sensor_calibration_date: formData.calibrationDate,
                    sensor_communication_protocol: formData.commProtocol,
                    sensor_firmware_version: formData.firmwareVersion,
                    sensor_metrics_supported: formData.metricsSupported,
                    sensor_operating_range: formData.sensorOperatingRange,
                    sensor_reporting_frequency_seconds: formData.reportingFreqSeconds ? Number(formData.reportingFreqSeconds) : undefined,
                    sensor_category: formData.sensorCategory,
                    sensor_position: formData.position,
                    sensor_wifi_enabled: formData.wifiEnabled === 'Yes',
                    sensor_associated_pole: formData.associatedPoleId,
                    created_by: user?.username || user?.user_id || user?.id,
                };
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sensor/create/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                setAlert({ type: 'success', message: 'Sensor asset created successfully!' });
                setTimeout(() => setAlert(null), 3000);
                setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();

            } else if (formData.categoryName === 'Cartridge') {
                const payload = {
                    cartridge_id: formData.id,
                    cartridge_description: formData.description,
                    cartridge_purchase_date: formData.purchaseDate,
                    cartridge_purchased_from: formData.purchaseFrom,
                    cartridge_brand: formData.brand,
                    cartridge_model: formData.model,
                    cartridge_vendor_contact_name: formData.vendorContact,
                    cartridge_vendor_email: formData.vendorEmail,
                    cartridge_vendor_phone_number: formData.vendorPhone,
                    cartridge_invoice_number: formData.invoiceNumber,
                    cartridge_po_number: formData.poNumber,
                    cartridge_currency: formData.currency,
                    cartridge_cost: formData.cost,
                    cartridge_warranty_start: formData.warrantyStart,
                    cartridge_warranty_end: formData.warrantyEnd,
                    cartridge_warranty_terms: formData.warrantyTerms,
                    cartridge_commissioned_date: formData.commissionedDate,
                    cartridge_installed_by: formData.installedBy,
                    cartridge_installed_on: formData.installedOn,
                    cartridge_installation_partner: formData.installationPartner,
                    cartridge_position: formData.position,
                    cartridge_type: formData.type,
                    cartridge_disposal_method: formData.disposalMethod,
                    cartridge_expected_life_hours: formData.expectedLifeHours ? Number(formData.expectedLifeHours) : undefined,
                    cartridge_media_description: formData.mediaDescription,
                    cartridge_replacement_threshold: formData.replacementThreshold,
                    cartridge_associated_pole: formData.associatedPoleId,
                    created_by: user?.username || user?.user_id || user?.id,
                };
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cartridge/create/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                setAlert({ type: 'success', message: 'Cartridge asset created successfully!' });
                setTimeout(() => setAlert(null), 3000);
                setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();

            } else if (formData.categoryName === 'LED Screen') {
                const payload = {
                    led_screen_id: formData.id,
                    led_screen_description: formData.description,
                    led_screen_purchase_date: formData.purchaseDate,
                    led_screen_purchased_from: formData.purchaseFrom,
                    led_screen_brand: formData.brand,
                    led_screen_model: formData.model,
                    led_screen_vendor_contact_name: formData.vendorContact,
                    led_screen_vendor_email: formData.vendorEmail,
                    led_screen_vendor_phone_number: formData.vendorPhone,
                    led_screen_invoice_number: formData.invoiceNumber,
                    led_screen_po_number: formData.poNumber,
                    led_screen_currency: formData.currency,
                    led_screen_cost: formData.cost,
                    led_screen_warranty_start: formData.warrantyStart,
                    led_screen_warranty_end: formData.warrantyEnd,
                    led_screen_warranty_terms: formData.warrantyTerms,
                    led_screen_commissioned_date: formData.commissionedDate,
                    led_screen_installed_by: formData.installedBy,
                    led_screen_installed_on: formData.installedOn,
                    led_screen_installation_partner: formData.installationPartner,
                    led_screen_connectivity: formData.ledConnectivity,
                    led_screen_controller_model: formData.ledControllerModel,
                    led_screen_brightness_nits: formData.brightnessNits ? Number(formData.brightnessNits) : undefined,
                    led_screen_position: formData.position,
                    led_screen_refresh_rate_hz: formData.ledRefreshRateHz ? Number(formData.ledRefreshRateHz) : undefined,
                    led_screen_resolution: formData.ledResolution,
                    led_screen_size: formData.ledSize,
                    led_screen_type: formData.type,
                    led_screen_associated_pole: formData.associatedPoleId,
                    created_by: user?.username || user?.user_id || user?.id,
                };
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/led-screen/create/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                setAlert({ type: 'success', message: 'LED Screen asset created successfully!' });
                setTimeout(() => setAlert(null), 3000);
                setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();

            } else if (formData.categoryName === 'LED Controller') {
                const payload = {
                    led_controller_id: formData.id,
                    led_controller_description: formData.description,
                    led_controller_purchase_date: formData.purchaseDate,
                    led_controller_purchased_from: formData.purchaseFrom,
                    led_controller_brand: formData.brand,
                    led_controller_model: formData.model,
                    led_controller_vendor_contact_name: formData.vendorContact,
                    led_controller_vendor_email: formData.vendorEmail,
                    led_controller_vendor_phone_number: formData.vendorPhone,
                    led_controller_invoice_number: formData.invoiceNumber,
                    led_controller_po_number: formData.poNumber,
                    led_controller_currency: formData.currency,
                    led_controller_cost: formData.cost,
                    led_controller_warranty_start: formData.warrantyStart,
                    led_controller_warranty_end: formData.warrantyEnd,
                    led_controller_warranty_terms: formData.warrantyTerms,
                    led_controller_commissioned_date: formData.commissionedDate,
                    led_controller_installed_by: formData.installedBy,
                    led_controller_installed_on: formData.installedOn,
                    led_controller_installation_partner: formData.installationPartner,
                    led_controller_cloud_connected: formData.cloudConnected === 'Yes',
                    led_controller_communication_interface: formData.communicationInterface,
                    led_controller_control_mode: formData.controlMode,
                    led_controller_position: formData.position,
                    led_controller_input_interfaces: formData.inputInterfaces,
                    led_controller_max_pixel_capacity: formData.maxPixelCapacity ? Number(formData.maxPixelCapacity) : undefined,
                    led_controller_output_ports: formData.outputPorts ? Number(formData.outputPorts) : undefined,
                    led_controller_supported_resolution: formData.supportedResolution,
                    led_controller_associated_pole: formData.associatedPoleId,
                    created_by: user?.username || user?.user_id || user?.id,
                };
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/led-controller/create/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                setAlert({ type: 'success', message: 'LED Controller asset created successfully!' });
                setTimeout(() => setAlert(null), 3000);
                setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();

            } else if (formData.categoryName === 'LED MS Cabinet') {
                const payload = {
                    led_ms_cabinet_id: formData.id,
                    led_ms_cabinet_description: formData.description,
                    led_ms_cabinet_purchase_date: formData.purchaseDate,
                    led_ms_cabinet_purchased_from: formData.purchaseFrom,
                    led_ms_cabinet_brand: formData.brand,
                    led_ms_cabinet_model: formData.model,
                    led_ms_cabinet_vendor_contact_name: formData.vendorContact,
                    led_ms_cabinet_vendor_email: formData.vendorEmail,
                    led_ms_cabinet_vendor_phone_number: formData.vendorPhone,
                    led_ms_cabinet_invoice_number: formData.invoiceNumber,
                    led_ms_cabinet_po_number: formData.poNumber,
                    led_ms_cabinet_currency: formData.currency,
                    led_ms_cabinet_cost: formData.cost,
                    led_ms_cabinet_warranty_start: formData.warrantyStart,
                    led_ms_cabinet_warranty_end: formData.warrantyEnd,
                    led_ms_cabinet_warranty_terms: formData.warrantyTerms,
                    led_ms_cabinet_commissioned_date: formData.commissionedDate,
                    led_ms_cabinet_installed_by: formData.installedBy,
                    led_ms_cabinet_installed_on: formData.installedOn,
                    led_ms_cabinet_installation_partner: formData.installationPartner,
                    led_ms_cabinet_depth: formData.cabinetDepthMm,
                    led_ms_cabinet_height: formData.cabinetHeightMm,
                    led_ms_cabinet_width: formData.cabinetWidthMm,
                    led_ms_cabinet_type: formData.cabinetType,
                    led_ms_cabinet_protection: formData.corrosionProtection,
                    led_ms_cabinet_associated_pole: formData.associatedPoleId,
                    created_by: user?.username || user?.user_id || user?.id,
                };
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/led-ms-cabinet/create/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                setAlert({ type: 'success', message: 'LED MS Cabinet asset created successfully!' });
                setTimeout(() => setAlert(null), 3000);
                setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();

            } else if (formData.categoryName === 'LED Receiving Card') {
                const payload = {
                    led_receiving_card_id: formData.id,
                    led_receiving_card_description: formData.description,
                    led_receiving_card_purchase_date: formData.purchaseDate,
                    led_receiving_card_purchased_from: formData.purchaseFrom,
                    led_receiving_card_brand: formData.brand,
                    led_receiving_card_model: formData.model,
                    led_receiving_card_vendor_contact_name: formData.vendorContact,
                    led_receiving_card_vendor_email: formData.vendorEmail,
                    led_receiving_card_vendor_phone_number: formData.vendorPhone,
                    led_receiving_card_invoice_number: formData.invoiceNumber,
                    led_receiving_card_po_number: formData.poNumber,
                    led_receiving_card_currency: formData.currency,
                    led_receiving_card_cost: formData.cost,
                    led_receiving_card_warranty_start: formData.warrantyStart,
                    led_receiving_card_warranty_end: formData.warrantyEnd,
                    led_receiving_card_warranty_terms: formData.warrantyTerms,
                    led_receiving_card_commissioned_date: formData.commissionedDate,
                    led_receiving_card_installed_by: formData.installedBy,
                    led_receiving_card_installed_on: formData.installedOn,
                    led_receiving_card_installation_partner: formData.installationPartner,
                    led_receiving_card_associated_pole: formData.associatedPoleId,
                    created_by: user?.username || user?.user_id || user?.id,
                };
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/led-receiving-card/create/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                setAlert({ type: 'success', message: 'LED Receiving Card asset created successfully!' });
                setTimeout(() => setAlert(null), 3000);
                setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();

            } else if (formData.categoryName === 'LED SMPS') {
                const payload = {
                    led_smps_id: formData.id,
                    led_smps_description: formData.description,
                    led_smps_purchase_date: formData.purchaseDate,
                    led_smps_purchased_from: formData.purchaseFrom,
                    led_smps_brand: formData.brand,
                    led_smps_model: formData.model,
                    led_smps_vendor_contact_name: formData.vendorContact,
                    led_smps_vendor_email: formData.vendorEmail,
                    led_smps_vendor_phone_number: formData.vendorPhone,
                    led_smps_invoice_number: formData.invoiceNumber,
                    led_smps_po_number: formData.poNumber,
                    led_smps_currency: formData.currency,
                    led_smps_cost: formData.cost,
                    led_smps_warranty_start: formData.warrantyStart,
                    led_smps_warranty_end: formData.warrantyEnd,
                    led_smps_warranty_terms: formData.warrantyTerms,
                    led_smps_commissioned_date: formData.commissionedDate,
                    led_smps_installed_by: formData.installedBy,
                    led_smps_installed_on: formData.installedOn,
                    led_smps_installation_partner: formData.installationPartner,
                    led_smps_associated_pole: formData.associatedPoleId,
                    created_by: user?.username || user?.user_id || user?.id,
                };
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/led-smps/create/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                setAlert({ type: 'success', message: 'LED SMPS asset created successfully!' });
                setTimeout(() => setAlert(null), 3000);
                setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();

            } else if (formData.categoryName === 'Solar Battery') {
                const payload = {
                    solar_battery_id: formData.id,
                    solar_battery_description: formData.description,
                    solar_battery_purchase_date: formData.purchaseDate,
                    solar_battery_purchased_from: formData.purchaseFrom,
                    solar_battery_brand: formData.brand,
                    solar_battery_model: formData.model,
                    solar_battery_vendor_contact_name: formData.vendorContact,
                    solar_battery_vendor_email: formData.vendorEmail,
                    solar_battery_vendor_phone_number: formData.vendorPhone,
                    solar_battery_invoice_number: formData.invoiceNumber,
                    solar_battery_po_number: formData.poNumber,
                    solar_battery_currency: formData.currency,
                    solar_battery_cost: formData.cost,
                    solar_battery_warranty_start: formData.warrantyStart,
                    solar_battery_warranty_end: formData.warrantyEnd,
                    solar_battery_warranty_terms: formData.warrantyTerms,
                    solar_battery_commissioned_date: formData.commissionedDate,
                    solar_battery_installed_by: formData.installedBy,
                    solar_battery_installed_on: formData.installedOn,
                    solar_battery_installation_partner: formData.installationPartner,
                    solar_battery_associated_pole: formData.associatedPoleId,
                    created_by: user?.username || user?.user_id || user?.id,
                };
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/solar-battery/create/`, { method: 'POST', headers, body: JSON.stringify(payload) });
                if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || e.message || JSON.stringify(e)); }
                setAlert({ type: 'success', message: 'Solar Battery asset created successfully!' });
                setTimeout(() => setAlert(null), 3000);
                setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();

            } else {
                // Fallback for any unknown category
                setAlert({ type: 'success', message: 'Asset submitted successfully!' });
                setTimeout(() => setAlert(null), 3000);
                setViewMode('list'); setFormData(EMPTY_FORM); await fetchAssets();
            }
        } catch (err) {
            setAlert({ type: 'error', message: err.message || 'Failed to submit asset. Please try again.' });
            setTimeout(() => setAlert(null), 8000);
        }
    };


    const handleCancel = () => { setViewMode('list'); setFormData(EMPTY_FORM); setFormErrors({}); };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="assets-root"
        >
            <style>
                {`
                    ${Object.entries(formErrors)
                        .filter(([key, val]) => val)
                        .map(([key]) => `
                        [name="${key}"]:not([type="radio"]),
                        .al-datepick-wrap:has(input[name="${key}"]),
                        .al-radio-group:has(input[name="${key}"]) {
                            border: 1px solid #ff4d4f !important;
                            outline: 1px solid #ff4d4f !important;
                            box-shadow: 0 0 5px rgba(255, 77, 79, 0.3) !important;
                            border-radius: 4px;
                        }
                    `).join('')}
                `}
            </style>
            {/* Header */}
            <InnerHeader
                user={user}
                setView={setView}
                onProfilePhotoSuccess={async () => {
                    setTimeout(async () => { await fetchUserDetails(); }, 500);
                    setAlert({ type: 'success', message: 'Profile picture updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                }}
            />

            {/* Secondary Bar */}
            <div className="assets-secondary-bar">
                <div className="assets-title-wrap">
                    <div className="assets-plus-icon"><Plus size={13} /></div>
                    <span className="assets-page-title">ASSETS</span>
                </div>
                <div className="assets-top-nav">
                    {/* Navigation buttons commented out as requested - preserved for future use
                    {['Home', 'Dashboard', 'Analytics', 'Export Reports', 'Asset', 'Supplier', 'Pole Design', 'Maintenance']
                        .filter(label => {
                            if (label === 'Home') return true;
                            const role = user?.role;
                            const normRole = role ? role.replace(/[-_ ]/g, '').toLowerCase() : '';
                            if (label === 'Analytics' && normRole !== 'superadmin') return false;
                            if (normRole === 'technicalsupport') return true;
                            if (normRole === 'superadmin') return true;
                            if (normRole === 'government') return label === 'Dashboard';
                            if (normRole === 'maintenance' || normRole === 'research') {
                                return label === 'Dashboard' || label === 'Maintenance';
                            }
                            if (normRole === 'assetmanagement') {
                                return label !== 'Maintenance' && label !== 'Export Reports';
                            }
                            return true;
                        })
                        .map(label => (
                            <button
                                key={label}
                                className={`assets-top-nav-btn${activeTopNav === label ? ' active' : ''}`}
                                disabled={label === 'Export Reports'}
                                style={label === 'Export Reports' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                onClick={() => {
                                    setActiveTopNav(label);
                                    if (label === 'Home') setView && setView('home');
                                    else if (label === 'Dashboard') setView && setView('dashboard');
                                    else if (label === 'Analytics') setView && setView('analytics');
                                    else if (label === 'Asset') setView && setView('assets-list');
                                    else if (label === 'Supplier') setView && setView('supplier-list');
                                    else if (label === 'Pole Design') setView && setView('pole-designs');
                                    else if (label === 'Maintenance') setView && setView('maintenance');
                                }}
                            >
                                {label}
                            </button>
                        ))}
                    */}
                    <button
                        className="assets-top-nav-btn"
                        onClick={() => setView && setView('home')}
                        title="Back to Home"
                    >
                        ← Home
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="assets-body">
                <AssetsSidebar setView={setView} activeItem="Assets" />

                <div className="assets-main">
                    {/* Alert */}
                    {alert && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`assets-alert ${alert.type}`}
                        >
                            <CheckCircle size={18} />
                            <span className="alert-message-flex">{alert.message}</span>
                            <button className="assets-alert-close" onClick={() => setAlert(null)}>×</button>
                        </motion.div>
                    )}

                    {/* ── ADD / EDIT FORM ─────────────────────────────────────────────── */}
                    {viewMode === 'form' ? (
                        <motion.div
                            key="asset-form"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="al-form-card"
                        >

                            <div className='two-col-back-wrap'>
                                <div className='two-col-back-1'>  <h2 className="al-form-title">{editingAsset ? 'Edit Asset' : 'Add Asset'}</h2></div>
                                <div className='two-col-back-2'><div className='back'>
                                    <button className="loc-add-btn" onClick={handleCancel}>Back to Assets List</button></div></div>

                            </div>


                            {/* ── Category Section ── */}
                            <div className="al-two-col" style={{ gridTemplateColumns: (formData.categoryName && formData.categoryName !== 'Pole') ? '1fr 1fr' : '1fr' }}>
                                <Section title="Category" open={catOpen} onToggle={() => setCatOpen(o => !o)}>
                                    <div className="al-field-wrap">
                                        <select
                                            className={`al-select${formErrors.category ? ' error' : ''}`}
                                            name="categoryName"
                                            value={formData.categoryName}
                                            onChange={e => {
                                                const selectedName = e.target.value;
                                                const sel = categories.find(c => c.name === selectedName);
                                                setFormData(prev => ({
                                                    ...prev,
                                                    category: sel ? sel.id : '',
                                                    categoryName: selectedName,
                                                }));
                                                setFormErrors(prev => ({ ...prev, category: '' }));
                                            }}
                                        >
                                            <option value="" disabled>Select Category</option>
                                            {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                        </select>
                                        <ChevronDown size={14} className="al-select-icon" />
                                    </div>
                                    {formErrors.category && <p className="site-form-error">{formErrors.category}</p>}
                                </Section>

                                {/* Show Associated Pole ID for all categories except Pole */}
                                {formData.categoryName && formData.categoryName !== 'Pole' && (
                                    <Section title="Associated Pole ID" open={catOpen} onToggle={() => setCatOpen(o => !o)}>
                                        <div className="al-field-wrap">
                                            <select
                                                className="al-select"
                                                name="associatedPoleId"
                                                value={formData.associatedPoleId}
                                                onChange={handleChange}
                                            >
                                                <option value="" disabled>Select Pole ID</option>
                                                {polesList.length > 0
                                                    ? polesList.map(poleId => (
                                                        <option key={poleId} value={poleId}>{poleId}</option>
                                                    ))
                                                    : <option disabled>No poles available</option>
                                                }
                                            </select>
                                            <ChevronDown size={14} className="al-select-icon" />
                                        </div>
                                    </Section>
                                )}
                            </div>

                            {/* ── Core + Technical + Maintenance — two-column layout ── */}
                            <div className="al-two-col">

                                {/* LEFT — Core Details */}
                                <Section title="Core Details" open={coreOpen} onToggle={() => setCoreOpen(o => !o)}>
                                    <div >{/*className="al-row" */}
                                        {/* <input className="al-input al-auto-id" value={formData.categoryName === 'Pole' ? 'Pole ID-U-26-IN1' : formData.categoryName === 'Solar Light' ? 'Solar Light ID-U-26-IN1' : formData.autoId} readOnly /> */}
                                        {/* this for now we will use later */}
                                        <input className="al-input" name="id" maxLength={40} placeholder={formData.categoryName === 'Pole' ? "Pole ID *" : formData.categoryName === 'Solar Light' ? "Solar Light ID *" : "ID *"} value={formData.id} onChange={handleChange} readOnly={!!editingAsset} style={{ width: '100%', ...(editingAsset ? { opacity: 0.6, cursor: 'not-allowed', background: 'rgba(255,255,255,0.05)' } : {}) }} />

                                    </div>
                                    <textarea className="al-textarea" name="description" placeholder="Description *" value={formData.description} onChange={handleChange} rows={3} />
                                    <div className="al-row">
                                        <DatePicker name="purchaseDate" placeholder="Purchase Date *" value={formData.purchaseDate} onChange={handleChange} />
                                        <div className="al-field-wrap">
                                            <select
                                                className={`al-select${formErrors.purchaseFrom ? ' error' : ''}`}
                                                name="purchaseFrom"
                                                value={formData.purchaseFrom}
                                                onChange={handleSupplierChange}
                                            >
                                                <option value="" disabled>Purchase From *</option>
                                                {formData.purchaseFrom && !suppliersList.some(s => s.name === formData.purchaseFrom) && (
                                                    <option value={formData.purchaseFrom}>{formData.purchaseFrom}</option>
                                                )}
                                                {suppliersList.map(s => (
                                                    <option key={s.id || s.name} value={s.name}>
                                                        {s.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <ChevronDown size={13} className="al-select-icon" />
                                        </div>
                                    </div>
                                    <div className="al-row">
                                        <input className="al-input" name="brand" placeholder="Brand *" value={formData.brand} onChange={handleChange} />
                                        <input className="al-input" name="model" placeholder="Model *" value={formData.model} onChange={handleChange} />
                                    </div>
                                    {/* <div className="al-row">
                                        <input className="al-input" name="serialNo" placeholder="Serial No *" value={formData.serialNo} onChange={handleChange} />
                                        <input className="al-input" name="department" placeholder="Department" value={formData.department} onChange={handleChange} />
                                    </div> */}
                                    {/* <div className="al-row">
                                        <input className="al-input" name="assignedTo" placeholder="Assigned To" value={formData.assignedTo} onChange={handleChange} />
                                    </div> */}
                                    <div className="al-row">
                                        <input className="al-input" name="vendorContact" placeholder="Vendor Contact Name *" value={formData.vendorContact} onChange={handleChange} />
                                        <input className="al-input" name="vendorEmail" placeholder="Vendor Email *" value={formData.vendorEmail} onChange={handleChange} />
                                    </div>
                                    <div className="al-row">
                                        <input className="al-input" name="vendorPhone" placeholder="Vendor Phone Number *" value={formData.vendorPhone} onChange={handleChange} />
                                        <input className="al-input" name="invoiceNumber" placeholder="Invoice Number *" value={formData.invoiceNumber} onChange={handleChange} />
                                    </div>
                                    <div className="al-row">
                                        <div className="al-field-wrap">
                                            <input className="al-input" name="poNumber" placeholder="PO Number *" value={formData.poNumber} onChange={handleChange} />
                                            {/* Note: Downward chevron on PO Number might have been a UI misinterpretation, standard input used */}
                                        </div>
                                        <div className="al-field-wrap">
                                            <select className="al-select" name="currency" value={formData.currency} onChange={handleChange}>
                                                <option value="" disabled>Currency</option>
                                                <option value="INR">INR</option>
                                                <option value="USD">USD</option>
                                                <option value="EUR">EUR</option>
                                            </select>
                                            <ChevronDown size={13} className="al-select-icon" />
                                        </div>
                                    </div>
                                    <div className="al-row">
                                        <input className="al-input" name="cost" placeholder="Cost *" value={formData.cost} onChange={handleChange} />
                                        <DatePicker name="warrantyStart" placeholder="Warranty Start *" value={formData.warrantyStart} onChange={handleChange} />
                                    </div>
                                    <div className="al-row">
                                        <DatePicker name="warrantyEnd" placeholder="Warranty End *" value={formData.warrantyEnd} onChange={handleChange} />
                                        <input className="al-input" name="warrantyTerms" placeholder="Warranty Terms *" value={formData.warrantyTerms} onChange={handleChange} />
                                    </div>
                                    <div className="al-row">
                                        <DatePicker name="commissionedDate" placeholder="Commissioned Date *" value={formData.commissionedDate} onChange={handleChange} />
                                        <input className="al-input" name="installationPartner" placeholder="Installation Partner *" value={formData.installationPartner} onChange={handleChange} />
                                    </div>
                                    <div className="al-row">
                                        <input className="al-input" name="installedBy" placeholder="Installed By *" value={formData.installedBy} onChange={handleChange} />
                                        <DatePicker name="installedOn" placeholder="Installed On *" value={formData.installedOn} onChange={handleChange} />
                                    </div>

                                    {formData.categoryName === 'Pole' && (
                                        <>
                                            <div className="al-row">
                                                <div className="al-field-wrap">
                                                    <select className="al-select" name="selectSite" value={formData.selectSite} onChange={e => { handleChange(e); setFormData(prev => ({ ...prev, installationLocation: '' })); }}>
                                                        <option value="" disabled>Select Site *</option>
                                                        {sitesList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                                    </select>
                                                    <ChevronDown size={13} className="al-select-icon" />
                                                </div>
                                                <div className="al-field-wrap">
                                                    <select className="al-select" name="installationLocation" value={formData.installationLocation} onChange={handleChange}>
                                                        <option value="" disabled>Installation Location *</option>
                                                        {locationsList
                                                            .filter(l => !formData.selectSite || l.siteId === Number(formData.selectSite))
                                                            .map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                                                    </select>
                                                    <ChevronDown size={13} className="al-select-icon" />
                                                </div>
                                            </div>
                                            <div className="al-row">
                                                <input className="al-input" name="latitude" placeholder="Latitude *" value={formData.latitude} onChange={handleChange} />
                                                <input className="al-input" name="longitude" placeholder="Longitude *" value={formData.longitude} onChange={handleChange} />
                                            </div>
                                            <div className="al-row">
                                                <input className="al-input" type="number" name="fan_speed_rpm_cfm" placeholder="Fan Speed (RPM/CFM) *" value={formData.fan_speed_rpm_cfm} onChange={handleChange} />
                                                <div style={{ flex: 1 }}></div>
                                            </div>
                                        </>
                                    )}


                                </Section>

                                {/* RIGHT column — Technical + Maintenance */}
                                <div className="al-right-col">
                                    {/* Technical Details */}
                                    <Section title="Technical Details" open={techOpen} onToggle={() => setTechOpen(o => !o)}>
                                        {formData.categoryName === 'Pole' && (
                                            <>
                                                <div className="al-row">
                                                    <input className="al-input" name="foundationType" placeholder="Foundation Type *" value={formData.foundationType} onChange={handleChange} />
                                                    <div className="al-field-wrap">
                                                        <select className="al-select" name="poleHeight" value={formData.poleHeight} onChange={handleChange}>
                                                            <option value="" disabled>Pole Height *</option>
                                                            <option value="8 Meters">8 Meters</option>
                                                            <option value="6 Meters">6 Meters</option>
                                                            <option value="5.4 Meters">5.4 Meters</option>
                                                        </select>
                                                        <ChevronDown size={13} className="al-select-icon" />
                                                    </div>
                                                </div>
                                                <div className="al-row">
                                                    <div className="al-field-wrap">
                                                        <select className="al-select" name="poleMaterial" value={formData.poleMaterial} onChange={handleChange}>
                                                            <option value="" disabled>Pole Material *</option>
                                                            <option value="GI">GI</option>
                                                            <option value="MS">MS</option>
                                                            <option value="Steel">Steel</option>
                                                            <option value="Others">Others</option>
                                                        </select>
                                                        <ChevronDown size={13} className="al-select-icon" />
                                                    </div>
                                                    <input className="al-input" name="windRating" placeholder="Wind Rating *" value={formData.windRating} onChange={handleChange} />
                                                </div>
                                                <div className="al-row al-radio-group-wrap">
                                                    <div className="al-radio-group">
                                                        <span className="al-radio-label">Camera Enabled *</span>
                                                        <label className="al-radio-item"><input type="radio" name="cameraEnabled" value="Yes" checked={formData.cameraEnabled === 'Yes'} onChange={handleChange} /> Yes</label>
                                                        <label className="al-radio-item"><input type="radio" name="cameraEnabled" value="No" checked={formData.cameraEnabled === 'No'} onChange={handleChange} /> No</label>
                                                    </div>
                                                    <div className="al-radio-group">
                                                        <span className="al-radio-label">Display Enabled *</span>
                                                        <label className="al-radio-item"><input type="radio" name="displayEnabled" value="Yes" checked={formData.displayEnabled === 'Yes'} onChange={handleChange} /> Yes</label>
                                                        <label className="al-radio-item"><input type="radio" name="displayEnabled" value="No" checked={formData.displayEnabled === 'No'} onChange={handleChange} /> No</label>
                                                    </div>
                                                </div>
                                                <div className="al-row al-radio-group-wrap">
                                                    <div className="al-radio-group">
                                                        <span className="al-radio-label">Solar Enabled *</span>
                                                        <label className="al-radio-item"><input type="radio" name="solarEnabled" value="Yes" checked={formData.solarEnabled === 'Yes'} onChange={handleChange} /> Yes</label>
                                                        <label className="al-radio-item"><input type="radio" name="solarEnabled" value="No" checked={formData.solarEnabled === 'No'} onChange={handleChange} /> No</label>
                                                    </div>
                                                    <div className="al-radio-group">
                                                        <span className="al-radio-label">Wifi Enabled *</span>
                                                        <label className="al-radio-item"><input type="radio" name="wifiEnabled" value="Yes" checked={formData.wifiEnabled === 'Yes'} onChange={handleChange} /> Yes</label>
                                                        <label className="al-radio-item"><input type="radio" name="wifiEnabled" value="No" checked={formData.wifiEnabled === 'No'} onChange={handleChange} /> No</label>
                                                    </div>
                                                </div>
                                                <div className="al-row al-radio-group-wrap">
                                                    <div className="al-radio-group" style={{ flex: 0.5 }}>
                                                        <span className="al-radio-label">Lighting Enabled *</span>
                                                        <label className="al-radio-item"><input type="radio" name="lightingEnabled" value="Yes" checked={formData.lightingEnabled === 'Yes'} onChange={handleChange} /> Yes</label>
                                                        <label className="al-radio-item"><input type="radio" name="lightingEnabled" value="No" checked={formData.lightingEnabled === 'No'} onChange={handleChange} /> No</label>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {formData.categoryName === 'Solar Light' && (
                                            <>
                                                <div className="al-row">
                                                    <div className="al-radio-group" style={{ flex: 1 }}>
                                                        <span className="al-radio-label" style={{ minWidth: '100px' }}>Auto Dimming *</span>
                                                        <label className="al-radio-item"><input type="radio" name="autoDimming" value="Yes" checked={formData.autoDimming === 'Yes'} onChange={handleChange} /> Yes</label>
                                                        <label className="al-radio-item"><input type="radio" name="autoDimming" value="No" checked={formData.autoDimming === 'No'} onChange={handleChange} /> No</label>
                                                    </div>
                                                    <input className="al-input" name="batteryCapacity" placeholder="Battery Capacity *" value={formData.batteryCapacity} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" name="beamAngleDegree" placeholder="Beam Angle Degree *" value={formData.beamAngleDegree} onChange={handleChange} />
                                                    <div className="al-radio-group" style={{ flex: 1 }}>
                                                        <span className="al-radio-label" style={{ minWidth: '150px' }}>Charge Controller Mode *</span>
                                                        <label className="al-radio-item"><input type="radio" name="chargeControllerMode" value="Yes" checked={formData.chargeControllerMode === 'Yes'} onChange={handleChange} /> Yes</label>
                                                        <label className="al-radio-item"><input type="radio" name="chargeControllerMode" value="No" checked={formData.chargeControllerMode === 'No'} onChange={handleChange} /> No</label>
                                                    </div>
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" name="colorTemperature" placeholder="Color Temperature *" value={formData.colorTemperature} onChange={handleChange} />
                                                    <input className="al-input" name="lightPosition" placeholder="Light Position *" value={formData.lightPosition} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" name="lumens" placeholder="Lumens *" value={formData.lumens} onChange={handleChange} />
                                                    <input className="al-input" name="wattage" placeholder="Wattage *" value={formData.wattage} onChange={handleChange} />
                                                </div>
                                            </>
                                        )}

                                        {formData.categoryName === 'Rain Hood' && (
                                            <>
                                                <div className="al-row">
                                                    <select className="al-input" name="drainageDesign" value={formData.drainageDesign} onChange={handleChange}>
                                                        <option value="">Select Drainage Design *</option>
                                                        <option value="Yes">Yes</option>
                                                        <option value="No">No</option>
                                                    </select>
                                                    <input className="al-input" type="text" name="hoodType" placeholder="Hood Type *" value={formData.hoodType} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <select className="al-input" name="hoodMaterial" value={formData.hoodMaterial} onChange={handleChange}>
                                                        <option value="">Select Material *</option>
                                                        <option value="GI">GI</option>
                                                        <option value="MS">MS</option>
                                                        <option value="Steel">Steel</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </div>
                                            </>
                                        )}

                                        {formData.categoryName === 'Fan' && (
                                            <>
                                                <div className="al-row">
                                                    <input className="al-input" type="text" name="airFlow" placeholder="Air Flow *" value={formData.airFlow} onChange={handleChange} />
                                                    <input className="al-input" type="text" name="operatingTempRange" placeholder="Operating Temperature Range *" value={formData.operatingTempRange} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <select className="al-input" name="position" value={formData.position} onChange={handleChange}>
                                                        <option value="">Select Position *</option>
                                                        <option value="Fan1">Fan1</option>
                                                        <option value="Fan2">Fan2</option>
                                                    </select>
                                                    <input className="al-input" type="text" name="fanPower" placeholder="Power *" value={formData.fanPower} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" type="number" name="fanRatedRpm" placeholder="Rated RPM *" value={formData.fanRatedRpm} onChange={handleChange} />
                                                    <select className="al-input" name="fanRole" value={formData.fanRole} onChange={handleChange}>
                                                        <option value="">Select Role *</option>
                                                        <option value="Suction">Suction</option>
                                                        <option value="Exhaust">Exhaust</option>
                                                    </select>
                                                </div>
                                                <div className="al-row">
                                                    <select className="al-input" name="speedControl" value={formData.speedControl} onChange={handleChange}>
                                                        <option value="">Select Speed Control *</option>
                                                        <option value="PWM">PWM</option>
                                                        <option value="Closed Loop">Closed Loop</option>
                                                        <option value="Fixed">Fixed</option>
                                                    </select>
                                                    <select className="al-input" name="type" value={formData.type} onChange={handleChange}>
                                                        <option value="">Select Type *</option>
                                                        <option value="DC">DC</option>
                                                        <option value="AC">AC</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </div>
                                                <div className="al-row">
                                                    <div className="al-radio-group" style={{ flex: 1 }}>
                                                        <span className="al-radio-label">WiFi Enabled *</span>
                                                        <label className="al-radio-item"><input type="radio" name="wifiEnabled" value="Yes" checked={formData.wifiEnabled === 'Yes'} onChange={handleChange} /> Yes</label>
                                                        <label className="al-radio-item"><input type="radio" name="wifiEnabled" value="No" checked={formData.wifiEnabled === 'No'} onChange={handleChange} /> No</label>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {formData.categoryName === 'Security Camera' && (
                                            <>
                                                <div className="al-row">
                                                    <select className="al-input" name="cameraType" value={formData.cameraType} onChange={handleChange}>
                                                        <option value="">Select Camera Type *</option>
                                                        <option value="IP">IP</option>
                                                        <option value="Analog">Analog</option>
                                                        <option value="EdgeAI">EdgeAI</option>
                                                    </select>
                                                    <select className="al-input" name="cameraConnectivity" value={formData.cameraConnectivity} onChange={handleChange}>
                                                        <option value="">Select Connectivity *</option>
                                                        <option value="LAN">LAN</option>
                                                        <option value="WiFi">WiFi</option>
                                                        <option value="4G">4G</option>
                                                    </select>
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" type="number" name="frameRate" placeholder="Frame Rate *" value={formData.frameRate} onChange={handleChange} />
                                                    <div className="al-radio-group" style={{ flex: 1 }}>
                                                        <span className="al-radio-label">Night Vision *</span>
                                                        <label className="al-radio-item"><input type="radio" name="nightVision" value="Yes" checked={formData.nightVision === 'Yes'} onChange={handleChange} /> Yes</label>
                                                        <label className="al-radio-item"><input type="radio" name="nightVision" value="No" checked={formData.nightVision === 'No'} onChange={handleChange} /> No</label>
                                                    </div>
                                                </div>
                                                <div className="al-row">
                                                    <select className="al-input" name="cameraResolution" value={formData.cameraResolution} onChange={handleChange}>
                                                        <option value="">Select Resolution *</option>
                                                        <option value="Text">Text</option>
                                                        <option value="MP">MP</option>
                                                    </select>
                                                    <input className="al-input" type="number" name="retentionDays" placeholder="Retention Days *" value={formData.retentionDays} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <div className="al-radio-group" style={{ flex: 1 }}>
                                                        <span className="al-radio-label">WiFi Enabled *</span>
                                                        <label className="al-radio-item"><input type="radio" name="wifiEnabled" value="Yes" checked={formData.wifiEnabled === 'Yes'} onChange={handleChange} /> Yes</label>
                                                        <label className="al-radio-item"><input type="radio" name="wifiEnabled" value="No" checked={formData.wifiEnabled === 'No'} onChange={handleChange} /> No</label>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {formData.categoryName === 'Sensor' && (
                                            <>
                                                <div className="al-row">
                                                    <DatePicker name="calibrationDate" placeholder="Calibration Date *" value={formData.calibrationDate} onChange={handleChange} />
                                                    <select className="al-input" name="commProtocol" value={formData.commProtocol} onChange={handleChange}>
                                                        <option value="">Select Communication Protocol *</option>
                                                        <option value="MQTT">MQTT</option>
                                                        <option value="HTTPS">HTTPS</option>
                                                        <option value="RS485">RS485</option>
                                                        <option value="ModBus">ModBus</option>
                                                        <option value="LoRa">LoRa</option>
                                                        <option value="BLE">BLE</option>
                                                    </select>
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" type="text" name="firmwareVersion" placeholder="Firmware Version *" value={formData.firmwareVersion} onChange={handleChange} />
                                                    <select className="al-input" name="metricsSupported" value={formData.metricsSupported} onChange={handleChange}>
                                                        <option value="">Select Metrics Supported *</option>
                                                        <option value="PM20">PM20</option>
                                                        <option value="PM10">PM10</option>
                                                        <option value="PM2.5">PM2.5</option>
                                                        <option value="CO2">CO2</option>
                                                        <option value="NO2">NO2</option>
                                                        <option value="Methane">Methane</option>
                                                        <option value="Multi">Multi</option>
                                                    </select>
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" type="text" name="sensorOperatingRange" placeholder="Operating Range *" value={formData.sensorOperatingRange} onChange={handleChange} />
                                                    <input className="al-input" type="number" name="reportingFreqSeconds" placeholder="Reporting Frequency Seconds *" value={formData.reportingFreqSeconds} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <select className="al-input" name="sensorCategory" value={formData.sensorCategory} onChange={handleChange}>
                                                        <option value="">Select Category *</option>
                                                        <option value="PM20">PM20</option>
                                                        <option value="PM10">PM10</option>
                                                        <option value="PM2.5">PM2.5</option>
                                                        <option value="CO2">CO2</option>
                                                        <option value="NO2">NO2</option>
                                                        <option value="Methane">Methane</option>
                                                        <option value="Multi">Multi</option>
                                                    </select>
                                                    <select className="al-input" name="position" value={formData.position} onChange={handleChange}>
                                                        <option value="">Select Position *</option>
                                                        <option value="Inlet">Inlet</option>
                                                        <option value="Outlet">Outlet</option>
                                                    </select>
                                                </div>
                                                <div className="al-row">
                                                    <div className="al-radio-group" style={{ flex: 1 }}>
                                                        <span className="al-radio-label">WiFi Enabled *</span>
                                                        <label className="al-radio-item"><input type="radio" name="wifiEnabled" value="Yes" checked={formData.wifiEnabled === 'Yes'} onChange={handleChange} /> Yes</label>
                                                        <label className="al-radio-item"><input type="radio" name="wifiEnabled" value="No" checked={formData.wifiEnabled === 'No'} onChange={handleChange} /> No</label>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {formData.categoryName === 'Cartridge' && (
                                            <>
                                                <div className="al-row">
                                                    <select className="al-input" name="position" value={formData.position} onChange={handleChange}>
                                                        <option value="">Select Position *</option>
                                                        <option value="Cart1">Cart1</option>
                                                        <option value="Cart2">Cart2</option>
                                                        <option value="Cart3">Cart3</option>
                                                        <option value="Cart4">Cart4</option>
                                                    </select>
                                                    <select className="al-input" name="type" value={formData.type} onChange={handleChange}>
                                                        <option value="">Select Type *</option>
                                                        <option value="CO2 Media">CO2 Media</option>
                                                        <option value="PM Stage">PM Stage</option>
                                                        <option value="Chemical Media">Chemical Media</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" type="text" name="disposalMethod" placeholder="Disposal Method *" value={formData.disposalMethod} onChange={handleChange} />
                                                    <input className="al-input" type="number" name="expectedLifeHours" placeholder="Expected Life Hours *" value={formData.expectedLifeHours} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" type="text" name="mediaDescription" placeholder="Media Description *" value={formData.mediaDescription} onChange={handleChange} />
                                                    <input className="al-input" type="text" name="replacementThreshold" placeholder="Replacement Threshold *" value={formData.replacementThreshold} onChange={handleChange} />
                                                </div>
                                            </>
                                        )}

                                        {formData.categoryName === 'LED Screen' && (
                                            <>
                                                <div className="al-row">
                                                    <select className="al-input" name="ledConnectivity" value={formData.ledConnectivity} onChange={handleChange}>
                                                        <option value="">Select Connectivity *</option>
                                                        <option value="LAN">LAN</option>
                                                        <option value="WiFi">WiFi</option>
                                                        <option value="4G">4G</option>
                                                        <option value="RS485">RS485</option>
                                                    </select>
                                                    <input className="al-input" type="text" name="ledControllerModel" placeholder="Controller Model *" value={formData.ledControllerModel} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" type="number" name="brightnessNits" placeholder="Brightness Nits *" value={formData.brightnessNits} onChange={handleChange} />
                                                    <select className="al-input" name="position" value={formData.position} onChange={handleChange}>
                                                        <option value="">Select Position *</option>
                                                        <option value="Screen1">Screen1</option>
                                                        <option value="Screen2">Screen2</option>
                                                        <option value="Screen3">Screen3</option>
                                                    </select>
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" type="number" name="ledRefreshRateHz" placeholder="Refresh Rate Hz *" value={formData.ledRefreshRateHz} onChange={handleChange} />
                                                    <input className="al-input" type="text" name="ledResolution" placeholder="Resolution *" value={formData.ledResolution} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" type="text" name="ledSize" placeholder="Size *" value={formData.ledSize} onChange={handleChange} />
                                                    <select className="al-input" name="type" value={formData.type} onChange={handleChange}>
                                                        <option value="">Select Type *</option>
                                                        <option value="LED">LED</option>
                                                        <option value="LCD">LCD</option>
                                                        <option value="ePaper">ePaper</option>
                                                    </select>
                                                </div>
                                            </>
                                        )}

                                        {formData.categoryName === 'LED Receiving Card' && (
                                            <>
                                                <div className="al-row">
                                                    <div className="al-field-wrap">
                                                        <select className="al-select" name="communicationType" value={formData.communicationType} onChange={handleChange}>
                                                            <option value="" disabled>Communication Type *</option>
                                                            <option value="Ethernet">Ethernet</option>
                                                            <option value="WiFi">WiFi</option>
                                                            <option value="Fiber">Fiber</option>
                                                            <option value="LVDS">LVDS</option>
                                                            <option value="Hybrid">Hybrid</option>
                                                        </select>
                                                        <ChevronDown size={13} className="al-select-icon" />
                                                    </div>
                                                    <input className="al-input" name="maxResolutionSupported" placeholder="Max Resolution Supported *" value={formData.maxResolutionSupported} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <div className="al-field-wrap">
                                                        <select className="al-select" name="position" value={formData.position} onChange={handleChange}>
                                                            <option value="" disabled>Position *</option>
                                                            <option value="Module1">Module1</option>
                                                            <option value="Module2">Module2</option>
                                                            <option value="Module3">Module3</option>
                                                        </select>
                                                        <ChevronDown size={13} className="al-select-icon" />
                                                    </div>
                                                    <input className="al-input" name="refreshRateHz" placeholder="Refresh Rate Hz *" value={formData.refreshRateHz} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" type="number" name="supportedPixelCapacity" placeholder="Supported Pixel Capacity *" value={formData.supportedPixelCapacity} onChange={handleChange} />
                                                    <div className="al-radio-group" style={{ flex: 1 }}>
                                                        <span className="al-radio-label">Wifi Enabled *</span>
                                                        <label className="al-radio-item"><input type="radio" name="wifiEnabled" value="Yes" checked={formData.wifiEnabled === 'Yes'} onChange={handleChange} /> Yes</label>
                                                        <label className="al-radio-item"><input type="radio" name="wifiEnabled" value="No" checked={formData.wifiEnabled === 'No'} onChange={handleChange} /> No</label>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {formData.categoryName === 'LED SMPS' && (
                                            <>
                                                <div className="al-row">
                                                    <input className="al-input" type="number" name="expectedLifeHours" placeholder="Expected Life Hours *" value={formData.expectedLifeHours} onChange={handleChange} />
                                                    <input className="al-input" type="text" name="model" placeholder="Model *" value={formData.model} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <div className="al-field-wrap">
                                                        <select className="al-select" name="position" value={formData.position} onChange={handleChange}>
                                                            <option value="" disabled>Position *</option>
                                                            <option value="smps1">SMPS1</option>
                                                            <option value="smps2">SMPS2</option>
                                                            <option value="smps3">SMPS3</option>
                                                            <option value="Spare">Spare</option>
                                                        </select>
                                                        <ChevronDown size={13} className="al-select-icon" />
                                                    </div>
                                                    <div className="al-radio-group" style={{ flex: 1 }}>
                                                        <span className="al-radio-label">Wifi Enabled *</span>
                                                        <label className="al-radio-item"><input type="radio" name="wifiEnabled" value="Yes" checked={formData.wifiEnabled === 'Yes'} onChange={handleChange} /> Yes</label>
                                                        <label className="al-radio-item"><input type="radio" name="wifiEnabled" value="No" checked={formData.wifiEnabled === 'No'} onChange={handleChange} /> No</label>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {formData.categoryName === 'LED MS Cabinet' && (
                                            <>
                                                <div className="al-row">
                                                    <input className="al-input" name="cabinetDepthMm" placeholder="Cabinet Depth mm *" value={formData.cabinetDepthMm} onChange={handleChange} />
                                                    <input className="al-input" name="cabinetHeightMm" placeholder="Cabinet Height mm *" value={formData.cabinetHeightMm} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <div className="al-field-wrap">
                                                        <select className="al-select" name="cabinetType" value={formData.cabinetType} onChange={handleChange}>
                                                            <option value="" disabled>Cabinet Type *</option>
                                                            <option value="Indoor">Indoor</option>
                                                            <option value="Outdoor">Outdoor</option>
                                                            <option value="Semi Outdoor">Semi Outdoor</option>
                                                        </select>
                                                        <ChevronDown size={13} className="al-select-icon" />
                                                    </div>
                                                    <input className="al-input" name="cabinetWidthMm" placeholder="Cabinet Width mm *" value={formData.cabinetWidthMm} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <div className="al-field-wrap">
                                                        <select className="al-select" name="corrosionProtection" value={formData.corrosionProtection} onChange={handleChange}>
                                                            <option value="" disabled>Corrosion Protection *</option>
                                                            <option value="PowderCoated">PowderCoated</option>
                                                            <option value="Galvanized">Galvanized</option>
                                                            <option value="EpoxyCoated">EpoxyCoated</option>
                                                            <option value="None">None</option>
                                                        </select>
                                                        <ChevronDown size={13} className="al-select-icon" />
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {formData.categoryName === 'Solar Battery' && (
                                            <>
                                                <div className="al-row">
                                                    <div className="al-field-wrap">
                                                        <select className="al-select" name="managementSystem" value={formData.managementSystem} onChange={handleChange}>
                                                            <option value="" disabled>Management System *</option>
                                                            <option value="Integrated">Integrated</option>
                                                            <option value="External">External</option>
                                                            <option value="None">None</option>
                                                        </select>
                                                        <ChevronDown size={13} className="al-select-icon" />
                                                    </div>
                                                    <div className="al-field-wrap">
                                                        <select className="al-select" name="position" value={formData.position} onChange={handleChange}>
                                                            <option value="" disabled>Position *</option>
                                                            <option value="Battery1">Battery1</option>
                                                            <option value="Battery2">Battery2</option>
                                                            <option value="Aux Battery">Aux Battery</option>
                                                            <option value="Backup Battery">Backup Battery</option>
                                                        </select>
                                                        <ChevronDown size={13} className="al-select-icon" />
                                                    </div>
                                                </div>
                                                <div className="al-row">
                                                    <div className="al-field-wrap">
                                                        <select className="al-select" name="type" value={formData.type} onChange={handleChange}>
                                                            <option value="" disabled>Type *</option>
                                                            <option value="LiFePO4">LiFePO4</option>
                                                            <option value="Lithium-Ion">Lithium-Ion</option>
                                                            <option value="LeadAcid">LeadAcid</option>
                                                            <option value="SolidState">SolidState</option>
                                                            <option value="Other">Other</option>
                                                        </select>
                                                        <ChevronDown size={13} className="al-select-icon" />
                                                    </div>
                                                    <input className="al-input" name="capacityAh" placeholder="Capacity Ah *" value={formData.capacityAh} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" name="capacityKwh" placeholder="Capacity Kwh *" value={formData.capacityKwh} onChange={handleChange} />
                                                    <input className="al-input" name="disposalMethod" placeholder="Disposal Method *" value={formData.disposalMethod} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" type="number" name="expectedLifeCycles" placeholder="Expected Life Cycles *" value={formData.expectedLifeCycles} onChange={handleChange} />
                                                    <input className="al-input" type="number" name="expectedLifeYears" placeholder="Expected Life Years *" value={formData.expectedLifeYears} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" name="maxChargeVoltage" placeholder="Max Charge Voltage *" value={formData.maxChargeVoltage} onChange={handleChange} />
                                                    <input className="al-input" name="maxDischargeCurrent" placeholder="Max Discharge Current *" value={formData.maxDischargeCurrent} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" name="nominalVoltage" placeholder="Nominal Voltage *" value={formData.nominalVoltage} onChange={handleChange} />
                                                    <input className="al-input" name="recyclingPartner" placeholder="Recycling Partner *" value={formData.recyclingPartner} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" name="replacementThreshold" placeholder="Replacement Threshold *" value={formData.replacementThreshold} onChange={handleChange} />
                                                </div>
                                            </>
                                        )}

                                        {formData.categoryName === 'LED Controller' && (
                                            <>
                                                <div className="al-row">
                                                    <div className="al-radio-group" style={{ flex: 1 }}>
                                                        <span className="al-radio-label">Cloud Connected *</span>
                                                        <label className="al-radio-item"><input type="radio" name="cloudConnected" value="Yes" checked={formData.cloudConnected === 'Yes'} onChange={handleChange} /> Yes</label>
                                                        <label className="al-radio-item"><input type="radio" name="cloudConnected" value="No" checked={formData.cloudConnected === 'No'} onChange={handleChange} /> No</label>
                                                    </div>
                                                    <div className="al-field-wrap">
                                                        <select className="al-select" name="communicationInterface" value={formData.communicationInterface} onChange={handleChange}>
                                                            <option value="" disabled>Communication Interface *</option>
                                                            <option value="Ethernet">Ethernet</option>
                                                            <option value="WiFi">WiFi</option>
                                                            <option value="4G">4G</option>
                                                            <option value="5G">5G</option>
                                                            <option value="Fiber">Fiber</option>
                                                            <option value="Hybrid">Hybrid</option>
                                                        </select>
                                                        <ChevronDown size={13} className="al-select-icon" />
                                                    </div>
                                                </div>
                                                <div className="al-row">
                                                    <div className="al-field-wrap">
                                                        <select className="al-select" name="controlMode" value={formData.controlMode} onChange={handleChange}>
                                                            <option value="" disabled>Control Mode *</option>
                                                            <option value="Sync">Sync</option>
                                                            <option value="Async">Async</option>
                                                            <option value="Hybrid">Hybrid</option>
                                                        </select>
                                                        <ChevronDown size={13} className="al-select-icon" />
                                                    </div>
                                                    <input className="al-input" name="controllerModel" placeholder="Controller Model *" value={formData.controllerModel} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <div className="al-field-wrap">
                                                        <select className="al-select" name="position" value={formData.position} onChange={handleChange}>
                                                            <option value="" disabled>Controller Position *</option>
                                                            <option value="Primary">Primary</option>
                                                            <option value="Secondary">Secondary</option>
                                                            <option value="Backup">Backup</option>
                                                            <option value="Redundant">Redundant</option>
                                                        </select>
                                                        <ChevronDown size={13} className="al-select-icon" />
                                                    </div>
                                                    <div className="al-field-wrap">
                                                        <select className="al-select" name="inputInterfaces" value={formData.inputInterfaces} onChange={handleChange}>
                                                            <option value="" disabled>Input Interfaces *</option>
                                                            <option value="HDMI">HDMI</option>
                                                            <option value="DVI">DVI</option>
                                                            <option value="VGA">VGA</option>
                                                            <option value="SDI">SDI</option>
                                                            <option value="DP">DP</option>
                                                            <option value="Multiple">Multiple</option>
                                                        </select>
                                                        <ChevronDown size={13} className="al-select-icon" />
                                                    </div>
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" type="number" name="maxPixelCapacity" placeholder="Max Pixel Capacity *" value={formData.maxPixelCapacity} onChange={handleChange} />
                                                    <input className="al-input" type="number" name="outputPorts" placeholder="Output Ports *" value={formData.outputPorts} onChange={handleChange} />
                                                </div>
                                                <div className="al-row">
                                                    <input className="al-input" name="supportedResolution" placeholder="Supported Resolution *" value={formData.supportedResolution} onChange={handleChange} />
                                                </div>
                                            </>
                                        )}


                                    </Section>

                                    {/* Maintenance Details */}
                                    <Section title="Maintenance Details" open={maintOpen} onToggle={() => setMaintOpen(o => !o)}>
                                        <div className="al-row">
                                            <DatePicker name="lastInspectionDate" placeholder="Last Inspection Date" value={formData.lastInspectionDate} onChange={handleChange} />
                                            <DatePicker name="lastServiceDate" placeholder="Last Service Date" value={formData.lastServiceDate} onChange={handleChange} />
                                        </div>
                                        <div className="al-row">
                                            <DatePicker name="nextInspectionDue" placeholder="Next Inspection Due" value={formData.nextInspectionDue} onChange={handleChange} />
                                            <DatePicker name="nextServiceDate" placeholder="Next Service Date" value={formData.nextServiceDate} onChange={handleChange} />
                                        </div>
                                        <div className="al-row">
                                            <DatePicker name="removedOn" placeholder="Removed On" value={formData.removedOn} onChange={handleChange} />
                                            <input className="al-input" name="replacementAssetId" placeholder="Replacement Asset ID" value={formData.replacementAssetId} onChange={handleChange} />
                                        </div>
                                        <div className="al-row">
                                            <input className="al-input" name="notes" placeholder="Notes" value={formData.notes} onChange={handleChange} style={{ flex: 1 }} />
                                        </div>
                                    </Section>
                                </div>
                            </div>

                            {/* Form Actions */}
                            <div className="al-form-actions">
                                <button className="al-btn-draft" onClick={() => handleSubmit(true)}>Save Draft</button>
                                <button className="al-btn-submit" onClick={() => handleSubmit(false)}>Submit</button>
                                <button className="al-btn-cancel" onClick={handleCancel}>Cancel</button>
                            </div>
                        </motion.div>

                    ) : (

                        /* ── LIST VIEW ──────────────────────────────────────────────────── */
                        <div className="assets-card al-list-card">

                            {/* Toolbar */}
                            <div className="al-toolbar">
                                <div className="al-filter-wrap">
                                    <select
                                        className="loc-filter-select"
                                        value={filterCategory}
                                        onChange={e => { setFilterCategory(e.target.value); setCurrentPage(1); }}
                                    >
                                        <option value="">Select Category</option>
                                        {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                    </select>
                                    <ChevronDown size={13} className="loc-filter-icon" />
                                </div>

                                <div className="assets-search-wrap al-search">
                                    <Search size={14} color="#64748b" />
                                    <input
                                        className="assets-search-input"
                                        placeholder="Search"
                                        value={searchQuery}
                                        onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                                    />
                                </div>

                                <button className="loc-add-btn" onClick={openAddForm}>Add New Asset</button>
                            </div>

                            {/* Table */}
                            <div className="assets-table-wrap">
                                <table className="assets-table al-table">
                                    <thead>
                                        <tr>
                                            <th className="assets-th al-th-id">
                                                ID <span className="al-sort-icon">⇅</span>
                                            </th>
                                            <th className="assets-th al-th-status">Status</th>
                                            <th className="assets-th al-th-action">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {assets === null ? (
                                            [...Array(5)].map((_, i) => (
                                                <tr key={`skeleton-${i}`}>
                                                    <td colSpan={3} className="assets-td">
                                                        <div className="skeleton-loader" style={{ height: '24px', margin: '4px 0' }}>
                                                            <div className="skeleton-bar" style={{ height: '100%', width: '100%' }}></div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : paginated.length === 0 ? (
                                            <tr><td colSpan={3} className="assets-td assets-table-empty">No assets found.</td></tr>
                                        ) : paginated.map((asset, idx) => (
                                            <React.Fragment key={`${asset.id}-${idx}`}>
                                                {/* Parent row */}
                                                <tr
                                                    className="al-parent-row"
                                                    onClick={() => toggleExpand(assets.indexOf(asset))}
                                                >
                                                    <td className="assets-td al-td-parent">
                                                        <div className="al-td-parent-inner">
                                                            {asset.expanded
                                                                ? <ChevronDown size={13} className="al-chevron" />
                                                                : <ChevronRight size={13} className="al-chevron" />
                                                            }
                                                            <span
                                                                className={asset.expanded ? 'al-parent-id expanded' : 'al-parent-id'}
                                                                onClick={e => { e.stopPropagation(); openAssetPopup('Pole', asset.id); }}
                                                                style={{ cursor: 'pointer' }}
                                                            >


                                                                {asset.id}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="assets-td al-td-status">
                                                        <button
                                                            className={`al-toggle-btn${asset.status ? ' on' : ''}`}
                                                            onClick={(e) => toggleStatus(assets.indexOf(asset), e)}
                                                            title={asset.status ? 'Active' : 'Inactive'}
                                                        >
                                                            <span className="al-toggle-thumb" />
                                                        </button>
                                                    </td>
                                                    <td className="assets-td al-td-action"></td>
                                                </tr>
                                                {/* Child rows */}
                                                {asset.expanded && asset.children.map((child, cIdx) => (
                                                    <tr
                                                        key={`${child.id}-${cIdx}`}
                                                        className="al-child-row"
                                                    >
                                                        <td
                                                            className="assets-td al-td-child"
                                                            onClick={() => openAssetPopup(child.childType || 'Pole', child.id)}
                                                        >
                                                            {child.childType && child.childType !== 'Pole'
                                                                // ? `${child.childType} - ${child.id}`
                                                                ? `${child.id}`
                                                                : child.id}
                                                        </td>
                                                        <td className="assets-td al-td-status"></td>
                                                        <td className="assets-td al-td-action">
                                                            <span className="assets-action-group">
                                                                <button
                                                                    className="assets-action-btn edit"
                                                                    title="Edit"
                                                                    onClick={e => { e.stopPropagation(); openEditFormFetch(child); }}
                                                                >
                                                                    <Edit2 size={14} />
                                                                </button>
                                                                <button
                                                                    className="assets-action-btn delete"
                                                                    title="Delete"
                                                                    onClick={e => { e.stopPropagation(); setPendingDelete({ parentIdx: assets.indexOf(asset), childId: child.id }); }}
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="assets-pagination al-pagination">
                                <button className="assets-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>‹</button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                    <button
                                        key={page}
                                        className={`assets-page-btn${currentPage === page ? ' active' : ''}`}
                                        onClick={() => setCurrentPage(page)}
                                    >
                                        {page}
                                    </button>
                                ))}
                                <button className="assets-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>›</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Detail Popup ──────────────────────────────────────────────────────── */}
            <AnimatePresence>
                {popupAsset && (
                    <DetailPopup asset={popupAsset || {}} isLoading={popupLoading} onClose={() => setPopupAsset(null)} />
                )}
            </AnimatePresence>

            {/* ── Confirm Delete Modal ─────────────────────────────────────────────── */}
            <AnimatePresence>
                {pendingDelete && (
                    <ConfirmDeleteModal
                        message="Are you sure you want to delete this asset? This action cannot be undone."
                        onConfirm={handleDelete}
                        onCancel={() => setPendingDelete(null)}
                    />
                )}
            </AnimatePresence>

            <InnerFooter />
        </motion.div>
    );
};

export default AssetsList;
